import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  payInstallmentAsync,
  fetchInstallmentsByPlotIdAndStatus,
  fetchInstallmentsByPlotId,
  fetchActiveInstallmentsByBooking,
  fetchResellInstallmentsByBooking,
  updateInstallmentStatusAsync,
  fetchOverdueInstallments,
} from "../store/slices/installmentSlice";
import { useSidebarContext } from "../scenes/global/sidebar/SidebarContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchAllUsers,
  fetchCustomerPlots,
  clearPlots,
} from "../store/slices/authSlice";
import { fetchSocieties } from "../store/slices/societySlice";
import InstallmentBookingSlip from "./util/InstallmentBookingSlip";
import Modal from "react-modal";
import {
  MapPin,
  Home,
  Tag,
  CheckCircle,
  Info,
  HandCoins,
  BookCopy,
  ReceiptText,
  Edit2,
  X,
  ChevronLeft,
  ChevronRight,
  Clock10Icon,
  Coins,
  SigmaIcon,
  User,
  CalendarIcon,
  Eye,
  Phone,
  AlertTriangle,
} from "lucide-react";
import { getAllBookings } from "../store/slices/bookingSlice";
import CompletedInstallmentsPrint from "./util/CompletedInstallmentsPrint";
import CustomerDetailsModal from "./CustomerDetailsModal";
import FollowUpModal from "./FollowUpModal";
import { getPlotById, getUserById } from "../api";
import { fetchPlotById } from "../store/slices/plotSlice";

const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  backdropFilter: "blur(4px)",
  padding: "1rem",
};

const modalStyle = {
  position: "relative",
  background: "#ffffff",
  borderRadius: "8px",
  padding: "20px",
  width: "100%",
  maxWidth: "800px",
  maxHeight: "90vh",
  overflow: "auto",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
  zIndex: 1001,
};
const printStyles = `
  @media print {
    .print-btn, .close-btn {
      display: none !important;
    }
  }
`;

const calculateInstallmentDuration = (plotInstallments, plotBooking, plotTotals = null) => {
  if (!plotBooking) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize to start of day

  // Check if plot is fully paid (all money received)
  const isFullyPaid = plotTotals?.isFullyPaid || false;

  // Calculate total planned installments from booking
  const installmentYears = plotBooking.installment_years || 0;
  const installmentMonths = plotBooking.installment_months || 0;
  const totalPlanMonths = Math.round(installmentYears * 12) + installmentMonths;

  // Calculate total planned installments - prefer booking data, fallback to actual installments count
  let totalPlannedInstallments = totalPlanMonths;
  if (totalPlannedInstallments === 0 && plotInstallments && plotInstallments.length > 0) {
    // If booking doesn't have installment_years, use the actual number of installments
    totalPlannedInstallments = plotInstallments.length;
  }

  // If no installments exist yet, return basic info
  if (!plotInstallments || plotInstallments.length === 0) {
    return {
      totalPlanned: totalPlannedInstallments,
      completed: 0,
      remaining: totalPlannedInstallments,
      elapsedMonths: 0,
      remainingMonths: totalPlanMonths,
      planEndDate: null,
      formatDuration: (months) => {
        if (months === 0) return "0 months";
        const years = Math.floor(months / 12);
        const remainingMths = months % 12;
        if (years === 0) {
          return remainingMths === 1 ? "1 month" : `${remainingMths} months`;
        }
        if (remainingMths === 0) {
          return years === 1 ? "1 year" : `${years} years`;
        }
        const yearStr = years === 1 ? "year" : "years";
        const monthStr = remainingMths === 1 ? "month" : "months";
        return `${years} ${yearStr}, ${remainingMths} ${monthStr}`;
      },
    };
  }

  // Count completed installments
  const completedInstallments = plotInstallments.filter(
    (inst) => inst.status === "Completed"
  ).length;

  // If plot is fully paid, all installments are considered completed
  // This handles cases where plot is paid in full but not all installments were created
  const finalCompletedInstallments = isFullyPaid ? totalPlannedInstallments : completedInstallments;

  // Calculate remaining installments (planned - completed)
  // If fully paid, remaining should be 0
  const remainingInstallments = isFullyPaid ? 0 : Math.max(0, totalPlannedInstallments - completedInstallments);

  // Sort installments by due date to get first installment
  const sortedByDueDate = [...plotInstallments].sort(
    (a, b) => new Date(a.due_date) - new Date(b.due_date)
  );

  const firstInstallment = sortedByDueDate[0];
  const firstInstallmentDate = firstInstallment?.due_date;

  // Calculate plan end date: first installment due date + total plan duration
  let planEndDate = null;
  if (firstInstallmentDate && totalPlanMonths > 0) {
    planEndDate = new Date(firstInstallmentDate);
    planEndDate.setMonth(planEndDate.getMonth() + totalPlanMonths);
    planEndDate.setHours(0, 0, 0, 0);
  }

  // Calculate elapsed time from first installment due date to today
  let elapsedMonths = 0;
  if (firstInstallmentDate) {
    const startDate = new Date(firstInstallmentDate);
    startDate.setHours(0, 0, 0, 0);

    // More accurate month calculation - calculate ACTUAL elapsed time (don't cap it)
    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth();

    elapsedMonths = (todayYear - startYear) * 12 + (todayMonth - startMonth);

    // Adjust if today's day is before start date's day (not a full month yet)
    if (today.getDate() < startDate.getDate()) {
      elapsedMonths = Math.max(0, elapsedMonths - 1);
    }

    // DO NOT cap elapsed months - if plan ended years ago, show actual elapsed time
    // This is important for expired plans to show correct information
  }

  // Calculate remaining months: from today to plan end date (more accurate)
  let remainingMonths = 0;
  let isPlanExpired = false;

  if (planEndDate) {
    // Check if plan has expired
    isPlanExpired = today > planEndDate;

    if (isPlanExpired) {
      // Plan has expired - remaining months should be 0
      remainingMonths = 0;
    } else {
      // Plan is still active - calculate remaining months accurately
      const endYear = planEndDate.getFullYear();
      const endMonth = planEndDate.getMonth();
      const todayYear = today.getFullYear();
      const todayMonth = today.getMonth();

      remainingMonths = (endYear - todayYear) * 12 + (endMonth - todayMonth);

      // Adjust if today's day is after end date's day (already passed this month)
      if (today.getDate() > planEndDate.getDate()) {
        remainingMonths = Math.max(0, remainingMonths - 1);
      }

      // Ensure it's not negative
      remainingMonths = Math.max(0, remainingMonths);
    }
  } else if (totalPlanMonths > 0) {
    // Fallback: calculate from elapsed months if plan end date not available
    remainingMonths = Math.max(0, totalPlanMonths - elapsedMonths);
  }

  return {
    totalPlanned: totalPlannedInstallments,
    completed: finalCompletedInstallments,
    remaining: remainingInstallments,
    elapsedMonths: Math.max(0, elapsedMonths),
    remainingMonths: isFullyPaid ? 0 : Math.max(0, remainingMonths), // If fully paid, no remaining months
    planEndDate: planEndDate,
    isPlanExpired: isFullyPaid ? false : isPlanExpired, // If fully paid, plan is not expired
    isFullyPaid: isFullyPaid, // Pass through the fully paid status
    formatDuration: (months) => {
      if (months === 0) return "0 months";

      const years = Math.floor(months / 12);
      const remainingMths = months % 12;

      if (years === 0) {
        return remainingMths === 1 ? "1 month" : `${remainingMths} months`;
      }
      if (remainingMths === 0) {
        return years === 1 ? "1 year" : `${years} years`;
      }

      const yearStr = years === 1 ? "year" : "years";
      const monthStr = remainingMths === 1 ? "month" : "months";
      return `${years} ${yearStr}, ${remainingMths} ${monthStr}`;
    },
  };
};

const ManageInstallments = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const {
    installments,
    // PHASE-6: NEW BOOKING-AWARE STATE
    activeInstallments,
    resellCurrentOwnerInstallments,
    resellPreviousOwnerInstallments,
  } = useSelector((state) => state.installments);
  const users = useSelector((state) => state.auth.users);
  const customerPlots = useSelector((state) => state.auth.plots || []);
  const { societies } = useSelector((state) => state.societies);
  const { activeSociety, theme, gardient } = useSidebarContext();
  const [paymentMode, setPaymentMode] = useState("");
  const [installmentId, setInstallmentId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPlot, setSelectedPlot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [completedCurrentPage, setCompletedCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const [isSlipModalOpen, setIsSlipModalOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] = useState(null);
  const [printModalData, setPrintModalData] = useState(null);
  const [receiptNo, setReceiptNo] = useState("");
  const [selectedInstallmentDate, setSelectedInstallmentDate] = useState("");
  const [excessiveInstallments, setExcessiveInstallments] = useState([]);
  const [overdueInstallments, setOverdueInstallments] = useState([]);
  const [totals, setTotals] = useState({
    totalPaidAmount: 0,
    totalPendingInstallmentsAmount: 0,
    totalPlotAmount: 0,
  });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingInstallment, setEditingInstallment] = useState(null);
  const [editFormData, setEditFormData] = useState({
    installment_number: "",
    amount: "",
    due_date: "",
    description: "",
    payment_method: "",
    status: "",
  });
  const { bookings = [] } = useSelector((state) => state.booking);
  // console.log(bookings)
  const [filteredPlots, setFilteredPlots] = useState([]);
  const [plotSearchTerm, setPlotSearchTerm] = useState("");
  const [showPlotDropdown, setShowPlotDropdown] = useState(false);
  const [selectedPlotCustomer, setSelectedPlotCustomer] = useState(null);
  const dropdownRef = useRef(null);
  const [selectedInstallments, setSelectedInstallments] = useState([]);
  const [totalPaidAmount, setTotalPaidAmount] = useState(0);
  const [unpaidInstallments, setUnpaidInstallments] = useState([]);

  const [
    isCompletedInstallmentsPrintModalOpen,
    setIsCompletedInstallmentsPrintModalOpen,
  ] = useState(false);

  const [isCustomerDetailsModalOpen, setIsCustomerDetailsModalOpen] =
    useState(false);
  const [customerDetails, setCustomerDetails] = useState(null);

  const [isSaleHistoryModalOpen, setIsSaleHistoryModalOpen] = useState(false);
  const [saleHistory, setSaleHistory] = useState([]);
  const [saleHistoryLoading, setSaleHistoryLoading] = useState(false);

  // Follow-up Modal State
  const [isFollowUpModalOpen, setIsFollowUpModalOpen] = useState(false);
  const [selectedFollowUpData, setSelectedFollowUpData] = useState(null);

  // OPTIMIZATION: Cache installments per plot so card colors persist across plot selection changes
  const [plotInstallmentsCache, setPlotInstallmentsCache] = useState({});

  const selectedPlotFromRedux = useSelector(
    (state) => state.plots.selectedPlot
  );


  // Debug useEffect to monitor selectedInstallment changes
  useEffect(() => {
    if (selectedInstallment) {
      // console.log('SelectedInstallment changed:', selectedInstallment)
    }
  }, [selectedInstallment]);

  // Filter customer plots to only show plots from the active society
  // const filteredCustomerPlots = useMemo(() => {
  //   if (selectedCustomer && customerPlots && customerPlots.length > 0) {
  //     // Show all plots for the selected customer
  //     return customerPlots.filter(
  //       plot => plot.society_id?.toString() === activeSociety?._id?.toString()
  //     )
  //   }

  //   if (selectedCustomer && customerPlots && customerPlots.length > 0) {
  //     // Show all plots for the selected customer
  //     return customerPlots.filter(
  //       plot => plot.society_id?.toString() === activeSociety?._id?.toString()
  //     )
  //   }
  //   // Default: show all plots for the active society
  //   if (customerPlots && customerPlots.length > 0 && activeSociety) {
  //     return customerPlots.filter(
  //       plot => plot.society_id?.toString() === activeSociety?._id?.toString()
  //     )
  //   }
  //   return []
  // }, [selectedPlot, selectedCustomer, customerPlots, activeSociety])
  const filteredCustomerPlots = useMemo(() => {
    // If we have a selected customer (either directly or from plot search)
    const activeCustomer = selectedCustomer || selectedPlotCustomer;

    if (activeCustomer && customerPlots && customerPlots.length > 0) {
      // Show all plots for this customer in the active society
      return customerPlots.filter(
        (plot) => plot.society_id?.toString() === activeSociety?._id?.toString()
      );
    }

    // Default: show all plots for the active society
    if (customerPlots && customerPlots.length > 0 && activeSociety) {
      return customerPlots.filter(
        (plot) => plot.society_id?.toString() === activeSociety?._id?.toString()
      );
    }

    return [];
  }, [selectedCustomer, selectedPlotCustomer, customerPlots, activeSociety]);


  const handleEditClick = (installment) => {
    setEditingInstallment(installment);
    setEditFormData({
      installment_number: installment.installment_number,
      amount: installment.amount.toString(),
      due_date: new Date(installment.due_date).toISOString().split("T")[0],
      description: installment.description || "",
      status: installment.status,
      paid_amount: installment.paid_amount?.toString() || "",
      payment_date: installment.payment_date
        ? new Date(installment.payment_date).toISOString().split("T")[0]
        : "",
      receipt_no: installment.receipt_no || "",
    });
    setIsEditModalOpen(true);
  };

  // Add handler for form input changes
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSaveChanges = async () => {
    try {
      const updatedData = {
        installmentId: editingInstallment._id,
        status: editFormData.status,
        installment_number: editFormData.installment_number,
        amount: parseFloat(editFormData.amount),
        due_date: editFormData.due_date,
        description: editFormData.description,
        paid_amount: parseFloat(editFormData.paid_amount),
        payment_date: editFormData.payment_date,
        receipt_no: editFormData.receipt_no,
      };

      await dispatch(updateInstallmentStatusAsync(updatedData)).unwrap();
      // Refresh the installments list using booking-aware API
      if (selectedPlot) {
        const activeBooking = getActiveBookingForPlot(selectedPlot._id);
        if (activeBooking) {
          // Check if this is a resold plot by looking for transferred installments
          const hasTransferredInstallments = installments.some(
            (inst) =>
              inst.plot_id === selectedPlot._id &&
              inst.status === "Transferred" &&
              inst.transfer_type === "resell"
          );

          if (hasTransferredInstallments) {
            // Use resell API for plots with previous owners
            dispatch(fetchResellInstallmentsByBooking(activeBooking._id));
          } else {
            // Use active installments API for normal plots
            dispatch(fetchActiveInstallmentsByBooking(activeBooking._id));
          }
        }

        // BOOKING-AWARE: Update totals using booking data instead of deprecated API
        if (selectedCustomer) {
          const plotId = selectedPlot._id;
          const plotTotals = calculatePlotTotals(plotId);
          setTotals((prevTotals) => ({
            ...prevTotals,
            [plotId]: plotTotals,
          }));
        }
      }

      enqueueSnackbar("Installment updated successfully", {
        variant: "success",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });

      setIsEditModalOpen(false);
      setEditingInstallment(null);
    } catch (error) {
      enqueueSnackbar(error.message || "Failed to update installment", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  };
  useEffect(() => {
    dispatch(fetchAllUsers());
    dispatch(fetchSocieties());
  }, [dispatch]);

  const [lastFetchedSociety, setLastFetchedSociety] = useState(null);

  useEffect(() => {
    if (activeSociety?._id && activeSociety._id !== lastFetchedSociety) {
      dispatch(fetchOverdueInstallments());
      dispatch(
        getAllBookings({ payment_mode: "Full,Installment", limit: "all" })
      );
      setLastFetchedSociety(activeSociety._id);
    }
    console.log("DEBUG: Bookings in useEffect (374):", bookings);
    console.log("DEBUG: Selected Plot in useEffect (374):", selectedPlot);
  }, [dispatch, activeSociety?._id, lastFetchedSociety, bookings, selectedPlot]);

  // const searchedPlots = useMemo(() => {
  //   return filteredPlots.filter(
  //     plot => plot.plot_number.toLowerCase() === plotSearchTerm.toLowerCase()
  //   )
  // }, [filteredPlots, plotSearchTerm])
  const searchedPlots = useMemo(() => {
    // If there's a search term, search across ALL plots in the society
    if (plotSearchTerm) {
      return filteredPlots.filter(
        (plot) =>
          plot.plot_number.toLowerCase() === plotSearchTerm.toLowerCase()
      );
    }
    // If no search term, return empty array
    return [];
  }, [filteredPlots, plotSearchTerm]);

  useEffect(() => {
    if (activeSociety) {
      const validPlots = bookings
        .filter(
          (booking) =>
            (booking.payment_mode === "Installment" ||
              booking.payment_mode === "Full") &&
            booking.plot_id?.society_id?.toString() ===
            activeSociety?._id?.toString()
        )
        .map((booking) => booking.plot_id);

      setFilteredPlots(validPlots);
    }
  }, [bookings, activeSociety]);

  useEffect(() => {
    if (!isEditModalOpen) {
      setEditingInstallment(null);
    }
  }, [isEditModalOpen]);

  // useEffect(() => {
  //   if (selectedCustomer && selectedCustomer.customerData) {
  //     dispatch(fetchCustomerPlots(selectedCustomer.customerData._id))
  //   }
  // }, [dispatch, selectedCustomer])

  // REMOVED: Redundant useEffect - now handled by the optimized one above

  // Calculate totals for all customer plots immediately when customer is selected
  // This ensures instant color display even before installment data is loaded
  useEffect(() => {
    const activeCustomer = selectedCustomer || selectedPlotCustomer;
    if (activeCustomer && filteredCustomerPlots.length > 0) {
      console.log('DEBUG: Calculating initial totals for', filteredCustomerPlots.length, 'plots');
      filteredCustomerPlots.forEach((plot) => {
        const plotId = plot._id;
        const plotTotals = calculatePlotTotals(plotId);
        setTotals((prevTotals) => ({
          ...prevTotals,
          [plotId]: plotTotals,
        }));
      });
    }
  }, [selectedCustomer, selectedPlotCustomer, filteredCustomerPlots]);

  // Recalculate totals when installment data changes (for more accurate colors)
  useEffect(() => {
    if (filteredCustomerPlots.length > 0) {
      console.log('DEBUG: Recalculating totals due to installment data change');
      filteredCustomerPlots.forEach((plot) => {
        const plotId = plot._id;
        const plotTotals = calculatePlotTotals(plotId);
        setTotals((prevTotals) => ({
          ...prevTotals,
          [plotId]: plotTotals,
        }));
      });
    }
  }, [activeInstallments, resellCurrentOwnerInstallments, installments, filteredCustomerPlots]);

  // CACHE: Store installments per plot when they're loaded
  // This ensures card colors persist when switching between plots
  useEffect(() => {
    if (selectedPlot?._id) {
      const plotId = selectedPlot._id;

      // Combine all installment sources for this plot
      const allInstallmentSources = [
        ...(activeInstallments || []),
        ...(resellCurrentOwnerInstallments || []),
      ];

      // Filter and deduplicate installments for this plot
      const plotInstallments = allInstallmentSources.filter((inst) => {
        const instPlotId = inst.plot_id?._id || inst.plot_id;
        return instPlotId?.toString() === plotId?.toString() && inst.status !== 'Cancelled';
      });

      // Only update cache if we have installment data
      if (plotInstallments.length > 0) {
        // Remove duplicates by _id
        const uniqueInstallments = plotInstallments.reduce((acc, inst) => {
          if (!acc.find(item => item._id?.toString() === inst._id?.toString())) {
            acc.push(inst);
          }
          return acc;
        }, []);

        console.log('DEBUG: Caching installments for plot', plotId, '- Count:', uniqueInstallments.length);
        setPlotInstallmentsCache(prevCache => ({
          ...prevCache,
          [plotId]: uniqueInstallments
        }));
      }
    }
  }, [selectedPlot?._id, activeInstallments, resellCurrentOwnerInstallments]);

  // Clear the installments cache when customer changes (to avoid stale data)
  useEffect(() => {
    const activeCustomer = selectedCustomer || selectedPlotCustomer;
    if (activeCustomer) {
      console.log('DEBUG: Customer changed, clearing installments cache');
      setPlotInstallmentsCache({});
    }
  }, [selectedCustomer?._id, selectedPlotCustomer?._id]);

  // Add this useEffect after the existing ones
  useEffect(() => {
    if (selectedPlot && (selectedCustomer || selectedPlotCustomer)) {
      const customerId = selectedCustomer?._id || selectedPlotCustomer?._id;
      const plotId = selectedPlot._id;

      const plotTotals = calculatePlotTotals(plotId);
      setTotals((prevTotals) => ({
        ...prevTotals,
        [plotId]: plotTotals,
      }));
    }
  }, [selectedPlot, selectedCustomer, selectedPlotCustomer, dispatch]);

  // Add this function to handle showing customer details
  const handleShowCustomerDetails = async (customer) => {
    setIsSaleHistoryModalOpen(false); // Close the history modal before opening customer details
    let customerId = customer;
    if (typeof customer === "object") {
      customerId = customer._id || customer.id || customer;
    }
    let customerData = users.find((user) => user._id === customerId);
    if (!customerData) {
      try {
        customerData = await getUserById(customerId);
        // Wrap in { customerData: ... } for CustomerDetailsModal compatibility
        customerData = { customerData };
      } catch (e) {
        enqueueSnackbar("Customer details not available", {
          variant: "warning",
          anchorOrigin: { vertical: "bottom", horizontal: "right" },
        });
        return;
      }
    }
    if (customerData) {
      setCustomerDetails(customerData);
      setIsCustomerDetailsModalOpen(true);
    } else {
      enqueueSnackbar("Customer details not available", {
        variant: "warning",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  };

  useEffect(() => {
    if (selectedPlot) {
      const plotBooking = bookings.find(
        (booking) => booking.plot_id?._id === selectedPlot._id
      );

      if (plotBooking) {
        // Calculate planned installments using installment_years
        const plannedInstallments = plotBooking.installment_years
          ? plotBooking.installment_years * 12
          : 12; // Default to 12 months if not set

        // Get all installments for this plot
        const allInstallments = installments.filter(
          (inst) => inst.plot_id === selectedPlot._id
        );

        const unpaidInstallments = allInstallments.filter(
          (inst) => inst.status !== "Completed"
        );

        setUnpaidInstallments(unpaidInstallments); // Store in state

        // 1️⃣ Find Overdue Installments (Only those marked as "Overdue")
        const overdue = unpaidInstallments.filter(
          (installment) => installment.status === "Overdue"
        );
        setOverdueInstallments(overdue);

        // 2️⃣ Calculate Excessive Installments Based on `installment_years`
        const totalInstallments = allInstallments.length;

        if (totalInstallments > plannedInstallments) {
          // Ensure we only count unpaid excessive installments
          const unpaidExtraInstallments =
            unpaidInstallments.slice(plannedInstallments).length;

          // Show alert only if unpaid excessive installments exist
          setExcessiveInstallments(unpaidExtraInstallments);
        } else {
          setExcessiveInstallments(0); // Reset when within limit
        }
      }
    }
  }, [selectedPlot, installments, bookings]);
  const handleOutsideClick = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setShowPlotDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  // PHASE-6: NEW BOOKING-AWARE INSTALLMENT FETCHING (SECURITY FIX)
  const getActiveBookingForPlot = (plotId) => {
    console.log("DEBUG: getActiveBookingForPlot called with plotId:", plotId);
    const foundBooking = bookings.find(
      (booking) =>
        booking.plot_id?._id === plotId &&
        (booking.booking_status === "Booked" ||
          booking.booking_status === "Sold") // Only active bookings including Sold items
    );
    console.log("DEBUG: getActiveBookingForPlot found booking:", foundBooking);
    return foundBooking;
  };

  // PHASE-6: NEW BOOKING-AWARE INSTALLMENT FILTERING
  // Determine which installments to use based on plot type
  const activeBooking = selectedPlot ? getActiveBookingForPlot(selectedPlot._id) : null;
  const isResoldPlot = activeBooking && (
    resellCurrentOwnerInstallments.length > 0 || resellPreviousOwnerInstallments.length > 0
  );

  // Process installment for display - handle negative remaining amounts
  const processInstallmentForDisplay = (installment) => {
    const actualRemaining = installment.remaining_amount || 0;
    const displayRemaining = Math.max(0, actualRemaining);
    const excessPayment = actualRemaining < 0 ? Math.abs(actualRemaining) : 0;

    return {
      ...installment,
      remaining_amount: displayRemaining,
      actual_remaining_amount: actualRemaining,
      excess_payment: excessPayment
    };
  };

  const rawCurrentInstallments = isResoldPlot
    ? resellCurrentOwnerInstallments
    : activeInstallments;

  // Filter out cancelled installments before processing
  // Redux state now properly isolates installments by plot, so no additional filtering needed
  const filteredCurrentInstallments = rawCurrentInstallments.filter(inst => inst.status !== 'Cancelled');
  const filteredResellPreviousOwnerInstallments = resellPreviousOwnerInstallments.filter(inst => inst.status !== 'Cancelled');

  // Process installments for display - handle negative remaining amounts
  const currentInstallments = filteredCurrentInstallments.map(processInstallmentForDisplay);
  const processedResellPreviousOwnerInstallments = filteredResellPreviousOwnerInstallments.map(processInstallmentForDisplay);

  // DEBUG: Log installment data (can be removed in production)
  // console.log('DEBUG: ManageInstallments - selectedPlot:', selectedPlot?._id, 'currentInstallments:', currentInstallments?.length || 0)

  const calculatePlotTotals = (plotId, updatedInstallment = null) => {
    try {
      console.log("DEBUG: calculatePlotTotals called for plotId:", plotId);
      const activeBooking = getActiveBookingForPlot(plotId);
      if (!activeBooking) {
        console.warn('DEBUG: No active booking found for plot (calculatePlotTotals):', plotId);
        return {
          totalPaidAmount: 0,
          totalPendingInstallmentsAmount: 0,
          totalPlotAmount: 0,
          originalTotalAmount: 0,
          totalConcessionAmount: 0,
          isConcessionApplied: false,
          isFullyPaid: false,
        };
      }

      // Calculate total paid: initial payment + completed installments
      const initialPayment = activeBooking.initial_payment || 0;

      // Get installments for this specific plot from all available sources
      // For portfolio display, we need to get installments for each plot, not just the selected one
      let installmentsToUse = [];

      // If this is the currently selected plot, use rawCurrentInstallments (more accurate)
      if (selectedPlot?._id === plotId) {
        installmentsToUse = [...rawCurrentInstallments];
        if (updatedInstallment) {
          // Replace the paid installment with the updated version from API response
          const existingIndex = installmentsToUse.findIndex(inst => inst._id === updatedInstallment._id);
          if (existingIndex !== -1) {
            installmentsToUse[existingIndex] = updatedInstallment;
          }
        }
      } else {
        // For other plots in portfolio, get installments from all available sources
        // Include cached data as fallback to preserve colors across plot switches
        const cachedInstallments = plotInstallmentsCache[plotId] || [];
        const allInstallmentSources = [
          ...(activeInstallments || []),
          ...(resellCurrentOwnerInstallments || []),
          ...(installments || []),
          ...cachedInstallments
        ];

        // Filter installments for this specific plot and exclude cancelled ones
        installmentsToUse = allInstallmentSources.filter(
          (inst) => {
            const instPlotId = inst.plot_id?._id || inst.plot_id;
            const matchesPlot = instPlotId?.toString() === plotId?.toString();
            const isNotCancelled = inst.status !== 'Cancelled';
            return matchesPlot && isNotCancelled;
          }
        );

        // Remove duplicates by _id
        const uniqueInstallments = installmentsToUse.reduce((acc, inst) => {
          if (!acc.find(item => item._id?.toString() === inst._id?.toString())) {
            acc.push(inst);
          }
          return acc;
        }, []);
        installmentsToUse = uniqueInstallments;
      }
      const completedInstallmentsAmount = installmentsToUse
        .filter(inst => inst.status === 'Completed')
        .reduce((sum, inst) => sum + (inst.paid_amount || inst.amount), 0);

      const totalPlotAmount = activeBooking.total_amount || 0; // Amount after concession
      const originalTotalAmount = activeBooking.original_total_amount || activeBooking.total_amount || 0; // Original amount before concession
      const totalConcessionAmount = activeBooking.total_concession_amount || 0;
      const isConcessionApplied = activeBooking.is_concession_applied || false;

      // FIX: Check if plot is Sold or Full Payment - if so, force fully paid status
      const isSoldOrFull = activeBooking.booking_status === 'Sold' || activeBooking.payment_mode === 'Full';

      let totalPaidAmount = initialPayment + completedInstallmentsAmount;
      let pendingAmount = Math.max(0, totalPlotAmount - totalPaidAmount);

      if (isSoldOrFull) {
        // Force fully paid for Sold/Full plots
        totalPaidAmount = totalPlotAmount;
        pendingAmount = 0;
      }

      const isFullyPaid = isSoldOrFull || (pendingAmount === 0 && totalPaidAmount > 0);

      console.log('DEBUG: Plot Card Calculation for', plotId, ':', {
        bookingId: activeBooking._id,
        initialPayment,
        completedInstallmentsAmount,
        totalPaidAmount,
        totalPlotAmount,
        originalTotalAmount,
        totalConcessionAmount,
        isConcessionApplied,
        pendingAmount,
        isFullyPaid,
        currentInstallmentsCount: currentInstallments.length,
        updatedInstallmentProvided: !!updatedInstallment,
        completedCount: installmentsToUse.filter(inst => inst.status === 'Completed').length,
        pendingCount: installmentsToUse.filter(inst => inst.status !== 'Completed').length
      });

      return {
        totalPaidAmount,
        totalPendingInstallmentsAmount: pendingAmount,
        totalPlotAmount,
        originalTotalAmount,
        totalConcessionAmount,
        isConcessionApplied,
        isFullyPaid,
      };
    } catch (error) {
      console.error('Error in calculatePlotTotals for plotId:', plotId, error);
      // Return safe defaults on error to prevent breaking the portfolio
      return {
        totalPaidAmount: 0,
        totalPendingInstallmentsAmount: 0,
        totalPlotAmount: 0,
        originalTotalAmount: 0,
        totalConcessionAmount: 0,
        isConcessionApplied: false,
        isFullyPaid: false,
      };
    }
  };

  // Update selected installments when currentInstallments changes
  useEffect(() => {
    const completedInstallments = currentInstallments.filter(
      (inst) => inst.status === "Completed"
    );
    setSelectedInstallments(completedInstallments);
    setTotalPaidAmount(
      completedInstallments.reduce((sum, inst) => sum + inst.paid_amount, 0)
    );
  }, [currentInstallments]);

  useEffect(() => {
    if (selectedPlot) {
      console.log('DEBUG: Selected plot:', selectedPlot._id, 'Available bookings:', bookings.length)
      console.log('DEBUG: Bookings for this plot:', bookings.filter(b => b.plot_id?._id === selectedPlot._id))

      const activeBooking = getActiveBookingForPlot(selectedPlot._id);
      console.log('DEBUG: Active booking found:', activeBooking?._id)

      if (activeBooking) {
        // Check if this is a resold plot by checking if there are previous bookings for this plot
        const plotBookings = bookings.filter(booking => booking.plot_id?._id === selectedPlot._id);
        const hasPreviousBookings = plotBookings.some(booking =>
          booking.booking_status === "Resold" || booking.booking_status === "Cancelled"
        );

        if (hasPreviousBookings) {
          // Use resell API for plots that have been resold/cancelled before
          dispatch(fetchResellInstallmentsByBooking(activeBooking._id));
        } else {
          // Use active installments API for normal plots
          dispatch(fetchActiveInstallmentsByBooking(activeBooking._id));
        }
      }
    }
  }, [dispatch, selectedPlot, bookings]);

  useEffect(() => {
    setSelectedCustomer(null);
    setSelectedPlot(null);
    // Clear customer plots when society changes
    dispatch(clearPlots());
  }, [activeSociety, dispatch]);

  useEffect(() => {
    Modal.setAppElement("#root");
  }, []);

  // OPTIMIZED: Consolidated customer plots fetching to prevent redundant calls
  useEffect(() => {
    const activeCustomer = selectedCustomer || selectedPlotCustomer;
    if (activeCustomer?._id) {
      console.log('DEBUG: Fetching plots for customer:', activeCustomer._id);
      dispatch(fetchCustomerPlots(activeCustomer._id));
    }
  }, [dispatch, selectedCustomer?._id, selectedPlotCustomer?._id]);

  // OPTIMIZATION: Removed bulk fetch - only fetch installments when a specific plot is clicked
  // This eliminates unnecessary API calls and improves performance

  const getSocietyAndPlotDetails = (plotId) => {
    const plotBooking = bookings.find(
      (booking) => booking.plot_id?._id === plotId
    );
    const offerTag = plotBooking?.offertag;

    for (let soc of societies) {
      const plot = soc.plots.find((plot) => plot._id === plotId);

      if (plot) {
        return {
          societyName: soc.name,
          societyLocation: soc.location,
          societyImage: soc.society_image,
          plotNumber: plot.plot_number,
          plotType: plot.plot_type,
          block: plot.block,
          marla: plot.marla,
          category: plot.category,
          bookingStatus: plot.booking_status,
          status: plot.status,
          description: plot.description,
          deposite: plot.initial_payment,
          Offer: offerTag,
          Date: plotBooking
            ? new Date(plotBooking.booking_date).toLocaleDateString("en-GB")
            : "N/A",
          bookingReceiptNo: plotBooking ? plotBooking.receipt_no : "N/A",
          installmentsYear: plotBooking?.installment_years
            ? (() => {
              const years = plotBooking.installment_years;
              const totalMonths = Math.round(years * 12);
              const yearsPart = Math.floor(totalMonths / 12);
              const monthsPart = totalMonths % 12;

              if (yearsPart === 0) {
                return `${monthsPart} Month${monthsPart !== 1 ? "s" : ""}`;
              } else if (monthsPart === 0) {
                return `${yearsPart} Year${yearsPart !== 1 ? "s" : ""}`;
              } else {
                return `${yearsPart} Year${yearsPart !== 1 ? "s" : ""
                  } ${monthsPart} Month${monthsPart !== 1 ? "s" : ""}`;
              }
            })()
            : "N/A",
          dealerCommission:
            plotBooking &&
              plotBooking.dealer_comission !== undefined &&
              plotBooking.dealer_comission !== null
              ? plotBooking.dealer_comission
              : "N/A",
          dealerName:
            plotBooking &&
              plotBooking.dealer_name !== undefined &&
              plotBooking.dealer_name !== null &&
              plotBooking.dealer_name !== ""
              ? plotBooking.dealer_name
              : "N/A",
          dealerPhone:
            plotBooking &&
              plotBooking.dealer_phone !== undefined &&
              plotBooking.dealer_phone !== null &&
              plotBooking.dealer_phone !== ""
              ? plotBooking.dealer_phone
              : "N/A",
        };
      }
      // console.log()
    }
    return null;
  };

  // OPTIMIZATION: Pre-calculate plot data to avoid expensive computations in render loop
  const plotCardsData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return filteredCustomerPlots.map((plot) => {
      // Get booking details for this plot
      const plotBooking = bookings.find(
        (booking) =>
          booking.plot_id?._id === plot._id &&
          (booking.booking_status === "Booked" ||
            booking.booking_status === "Sold")
      );

      // PHASE-6: Get installments for this specific plot using all available sources
      // FIRST: Check the cache for this plot's installments (persists across selections)
      const cachedInstallments = plotInstallmentsCache[plot._id] || [];

      // Combine all installment sources and filter by plot_id
      const allInstallmentSources = [
        ...(activeInstallments || []),
        ...(resellCurrentOwnerInstallments || []),
        ...(installments || []),
        ...cachedInstallments // Include cached data as fallback
      ];

      // Filter installments for this specific plot and exclude cancelled ones
      let plotInstallments = allInstallmentSources.filter(
        (inst) => {
          const instPlotId = inst.plot_id?._id || inst.plot_id;
          const matchesPlot = instPlotId?.toString() === plot._id?.toString();
          const isNotCancelled = inst.status !== 'Cancelled';
          return matchesPlot && isNotCancelled;
        }
      );

      // Remove duplicates by _id
      const uniqueInstallments = plotInstallments.reduce((acc, inst) => {
        if (!acc.find(item => item._id?.toString() === inst._id?.toString())) {
          acc.push(inst);
        }
        return acc;
      }, []);

      plotInstallments = uniqueInstallments;

      // Get installment years
      const installmentYears = plotBooking?.installment_years || 1;

      // Calculate plan end date from FIRST installment's due date
      let planEndDate = null;
      if (plotInstallments.length > 0) {
        // Sort by installment number to get the first one
        const sortedInstallments = [...plotInstallments].sort(
          (a, b) => a.installment_number - b.installment_number
        );
        const firstInstallment = sortedInstallments[0];

        if (firstInstallment?.due_date) {
          const firstDueDate = new Date(firstInstallment.due_date);
          planEndDate = new Date(firstDueDate);

          // Add installment years to first due date
          const totalMonths = Math.round(installmentYears * 12);
          planEndDate.setMonth(planEndDate.getMonth() + totalMonths);
        }
      }

      // Check if plan period has ended
      const isPlanExpired = planEndDate && today > planEndDate;

      const isSold = plotBooking?.booking_status === 'Sold';

      // Count unpaid installments - FORCE 0 for Sold plots
      const unpaidInstallments = isSold ? [] : plotInstallments.filter(
        (inst) => inst.status !== "Completed"
      );

      // Count overdue installments - FORCE 0 for Sold plots
      const overdueCount = isSold ? 0 : plotInstallments.filter(
        (inst) =>
          new Date(inst.due_date) < today &&
          inst.status !== "Completed"
      ).length;

      // Determine card styling based on available data
      // Combine status colors with selection highlight
      let baseCardClass = "";
      let selectionHighlight = "";
      let cardClass = ""; // Declare cardClass here

      const plotTotalsForCard = totals[plot._id] || {};

      // Check if we have installment data for this plot
      const hasInstallmentData = plotInstallments.length > 0;
      const isFullyPaid = plotTotalsForCard?.isFullyPaid || false;
      const isSelected = selectedPlot?._id === plot._id;

      // Base status colors
      if (isFullyPaid) {
        // GREEN: Fully paid plot
        baseCardClass = "bg-green-900/30 border-green-500 ring-2 ring-green-500 scale-[1.02] shadow-lg";
      } else if (hasInstallmentData && isPlanExpired && unpaidInstallments.length > 0) {
        // RED: Plan period expired + unpaid installments
        baseCardClass = "bg-red-900 border-red-500 ring-2 ring-red-500 scale-[1.02] shadow-lg";
      } else if (hasInstallmentData && overdueCount >= 3) {
        // YELLOW: 3+ overdue installments
        baseCardClass = "bg-yellow-900 border-yellow-500 ring-2 ring-yellow-500 scale-[1.02] shadow-lg";
      } else {
        // DEFAULT: Normal plot card
        baseCardClass = `${theme.tailwind.bgHover12} ${theme.tailwind.bg19} hover:scale-[1.02] hover:shadow-lg`;
      }

      // Add selection highlight on top of status color
      if (isSelected) {
        selectionHighlight = ` ring-4 ${theme.tailwind.ringColor1} scale-[1.03] sm:scale-[1.04] shadow-2xl`;
      }

      // Combine base color with selection highlight
      cardClass = baseCardClass + selectionHighlight;

      const details = getSocietyAndPlotDetails(plot._id);
      const plotTotals = totals[plot._id] || {};

      const adjustedTotalPaid =
        details?.status === "Sold" || plotTotals?.isFullyPaid
          ? (plotTotals?.totalPlotAmount || 0)
          : (plotTotals?.totalPaidAmount || 0);

      return {
        plot,
        plotBooking,
        plotInstallments,
        installmentYears,
        planEndDate,
        isPlanExpired,
        unpaidInstallments,
        overdueCount,
        cardClass,
        details,
        plotTotals,
        adjustedTotalPaid
      };
    });
  }, [filteredCustomerPlots, bookings, activeInstallments, resellCurrentOwnerInstallments, installments, selectedPlot, totals, plotInstallmentsCache]);

  const enrichedInstallments = selectedInstallments.map((installment) => {
    const plotBooking = bookings.find(
      (booking) => booking.plot_id?._id === installment.plot_id
    );
    const customer = plotBooking?.customer_id || {};
    // console.log("Customerization:", customer);
    return {
      ...installment,
      plotDetails: {
        plotNumber: selectedPlot?.plot_number || "N/A",
        plotType: selectedPlot?.plot_type || "N/A",
        block: selectedPlot?.block || "N/A",
        size: selectedPlot?.size || "N/A",
        marla: selectedPlot?.marla || "N/A",
      },
      customerDetails: {
        name: customer?.name || "N/A",
        phone: customer?.contactInfo?.phone || "N/A",
        cnic: customer?.contactInfo?.cnic || "N/A",
        address: customer?.contactInfo?.address || "N/A",
      },
    };
  });

  // Filter users from the active society
  const customerOptions = useMemo(() => {
    if (!activeSociety || !activeSociety.users) {
      return [];
    }
    return activeSociety.users
      .filter((user) => user.role === "Customer")
      .map((customer) => (
        <option
          key={customer._id}
          value={customer._id}
          className={`${theme.tailwind.bg27}`}
        >
          {customer.username}
        </option>
      ));
  }, [activeSociety]);

  const handlePaymentModeChange = useCallback((value) => {
    setPaymentMode(value);
    setSelectedCustomer(null);
    setSelectedPlot(null);
    setInstallmentId("");
    setPaymentAmount("");
  }, []);

  const handleCustomerChange = useCallback(
    (customerId) => {
      const customer = users.find((user) => user._id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setSelectedPlot(null);
      }
    },
    [users]
  );

  // const handlePlotSelect = plot => {
  //   const plotDetails = getSocietyAndPlotDetails(plot._id)
  //   // Find the booking for this plot to get customer info
  //   const plotBooking = bookings.find(
  //     booking => booking.plot_id?._id === plot._id
  //   )
  //   const customerObj = plotBooking?.customer_id

  //   if (plotDetails) {
  //     setSelectedPlot({
  //       ...plot,
  //       plot_number: plotDetails.plotNumber,
  //       soc: {
  //         name: plotDetails.societyName,
  //         location: plotDetails.societyLocation,
  //         society_image: plotDetails.societyImage
  //       }
  //     })

  //     setSelectedPlotCustomer(customerObj)
  //   }
  // }
  const handlePlotSelect = (plot) => {
    const plotDetails = getSocietyAndPlotDetails(plot._id);
    // Find the booking for this plot to get customer info
    const plotBooking = bookings.find(
      (booking) => booking.plot_id?._id === plot._id
    );
    const customerObj = plotBooking?.customer_id;

    if (plotDetails) {
      setSelectedPlot({
        ...plot,
        plot_number: plotDetails.plotNumber,
        soc: {
          name: plotDetails.societyName,
          location: plotDetails.societyLocation,
          society_image: plotDetails.societyImage,
        },
      });

      // IMPORTANT: Update the selected customer to match the plot's customer
      if (customerObj) {
        // Handle both cases: customerObj could be an object or just an ID
        let customerId;
        let fullCustomerObj;

        if (typeof customerObj === 'string' || customerObj._id) {
          // If it's an ID string or has _id, find the full customer object
          customerId = typeof customerObj === 'string' ? customerObj : customerObj._id;
          fullCustomerObj = users.find(user => user._id === customerId) || customerObj;
        } else {
          // If it's already a full object
          fullCustomerObj = customerObj;
          customerId = customerObj._id || customerObj.customerData?._id;
        }

        setSelectedPlotCustomer(fullCustomerObj);
        // Override the selected customer with the plot's customer
        setSelectedCustomer(fullCustomerObj);

        // Note: fetchCustomerPlots is now handled by the useEffect above
      }
    }

    // Clear the search term after selection
    setPlotSearchTerm("");
  };

  const handleInstallmentChange = useCallback(
    (installment) => {
      const plotDetails = getSocietyAndPlotDetails(selectedPlot._id);
      if (plotDetails) {
        const amountToSet =
          installment.remaining_amount > 0
            ? installment.remaining_amount
            : installment.amount;

        setInstallmentId(installment._id);
        setPaymentAmount(amountToSet.toString());
        setIsModalOpen(true);
        setSelectedPlot({
          ...selectedPlot,
          plotNumber: plotDetails.plotNumber,
          societyName: plotDetails.societyName,
        });
        setSelectedInstallmentDate(installment.payment_date);
      }
    },
    [selectedPlot]
  );

  const handlePaymentAmountChange = (e) => {
    // Remove commas and any invalid characters
    const value = e.target.value.replace(/,/g, "").replace(/\.\d{3,}/, "");
    let floatValue = parseFloat(value);

    // Ensure the value is a valid number
    if (value === "" || (!isNaN(floatValue) && floatValue >= 0)) {
      // Format the number with commas
      setPaymentAmount(value ? formatNumberWithCommas(floatValue) : "");
    }
  };

  const formatNumberWithCommas = (number) => {
    const parts = number.toString().split(".");
    // Add commas to the integer part
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  // Ensure preloaded value is formatted correctly
  useEffect(() => {
    if (paymentAmount && typeof paymentAmount === "string") {
      const unformattedValue = paymentAmount.replace(/,/g, ""); // Remove commas
      const floatValue = parseFloat(unformattedValue);
      if (!isNaN(floatValue)) {
        setPaymentAmount(formatNumberWithCommas(floatValue));
      }
    }
  }, [paymentAmount]);

  const handlePayment = async () => {
    if (
      !installmentId ||
      !paymentAmount ||
      parseFloat(paymentAmount.replace(/,/g, "")) <= 0 ||
      !paymentMode
    ) {
      enqueueSnackbar("Please fill in all required fields with valid values", {
        variant: "warning",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    const currentInstallment = currentInstallments.find(
      (inst) => inst._id === installmentId
    );
    if (!currentInstallment) {
      enqueueSnackbar("Selected installment not found. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
      return;
    }

    const paymentAmountFloat = parseFloat(paymentAmount.replace(/,/g, ""));
    const paymentData = {
      installment_id: installmentId,
      paid_amount: paymentAmountFloat,
      receipt_no: receiptNo || `RCP-${Date.now()}`,
      updated_due_date: selectedInstallmentDate,
      payment_method: paymentMode,
    };

    let updatedTotals = {}; // Declare outside try block for print modal access

    try {
      setLoading(true);
      const response = await dispatch(
        payInstallmentAsync(paymentData)
      ).unwrap();
      // console.log('Payment Response:', response)
      setOverdueInstallments((prevOverdue) =>
        prevOverdue.filter((inst) => inst._id !== installmentId)
      );

      const message =
        currentInstallment.status === "Overdue"
          ? `Overdue installment paid successfully!`
          : "Payment processed successfully!";

      enqueueSnackbar(message, {
        variant: "success",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });

      if (selectedPlot) {
        const activeBooking = getActiveBookingForPlot(selectedPlot._id);
        if (activeBooking) {
          // Check if this is a resold plot by looking for transferred installments
          const hasTransferredInstallments = installments.some(
            (inst) =>
              inst.plot_id === selectedPlot._id &&
              inst.status === "Transferred" &&
              inst.transfer_type === "resell"
          );

          if (hasTransferredInstallments) {
            // Use resell API for plots with previous owners
            dispatch(fetchResellInstallmentsByBooking(activeBooking._id));
          } else {
            // Use active installments API for normal plots
            dispatch(fetchActiveInstallmentsByBooking(activeBooking._id));
          }
        }

        // BOOKING-AWARE: Update totals using booking data instead of deprecated API
        // Calculate updated totals immediately for print modal
        if (selectedCustomer && selectedPlot) {
          const plotId = selectedPlot._id;
          // Pass the updated installment from the API response to calculate accurate totals
          const plotTotals = calculatePlotTotals(plotId, response.installment);
          console.log('DEBUG: Calculated plotTotals for print modal:', plotTotals);
          // Use plot ID as key for consistency with print modal expectations
          updatedTotals = { [plotId]: plotTotals };
          // Update the totals state immediately
          setTotals((prevTotals) => ({
            ...prevTotals,
            ...updatedTotals,
          }));
        }

        // Fallback: If calculation failed, use existing totals or provide defaults
        if (Object.keys(updatedTotals).length === 0) {
          console.log('DEBUG: No updatedTotals calculated, using fallback');
          const plotId = selectedPlot?._id;
          if (plotId && totals[plotId]) {
            updatedTotals = { [plotId]: totals[plotId] };
          } else {
            // Provide default values as last resort
            updatedTotals = selectedPlot?._id ? {
              [selectedPlot._id]: {
                totalPaidAmount: 0,
                totalPendingInstallmentsAmount: 0,
                totalPlotAmount: 0
              }
            } : {};
          }
        }
      }

      setLoading(false);
      setPaymentAmount("");
      setReceiptNo("");
      setPaymentMode("");
      setIsModalOpen(false);

      // Automatically open print modal after successful payment
      // Use the enriched printData from the API response (PHASE-6 FIX)
      if (response && response.printData) {
        const printData = response.printData;
        console.log('DEBUG: Received printData from API:', printData);
        console.log('DEBUG: Updated totals for print modal:', updatedTotals);

        // Use the populated customer data from the API response
        const transformedCustomer = {
          name: printData.customer?.name || "N/A",
          contactInfo: {
            phone: printData.customer?.contactInfo?.phone || "N/A",
            address: printData.customer?.contactInfo?.address || "N/A",
            cnic: printData.customer?.contactInfo?.cnic || "N/A",
          },
        };

        console.log('DEBUG: Transformed customer for print:', transformedCustomer);

        // Calculate excess payment for display purposes
        const actualRemaining = response.installment.remaining_amount || 0;
        const displayRemaining = Math.max(0, actualRemaining); // Never show negative
        const excessPayment = actualRemaining < 0 ? Math.abs(actualRemaining) : 0;

        // Create a complete installment object using the enriched printData
        const updatedInstallment = {
          ...response.installment,
          // Use the populated customer data from printData
          customer_id: transformedCustomer,
          // Use the populated plot data from printData
          plot_id: printData.plot,
          // Use payment data from printData
          payment_date: printData.payment_date,
          receipt_no: printData.receipt_no,
          payment_method: printData.payment_method,
          paid_amount: printData.amount,
          remaining_amount: displayRemaining, // Display 0 for overpayments
          actual_remaining_amount: actualRemaining, // Keep actual for calculations
          excess_payment: excessPayment, // Add excess payment amount
          // Include booking and society data
          booking: printData.booking,
          society: printData.society
        };

        // Ensure payment data uses the correct plot ID key
        // Use the plot ID from the installment data to match print modal access
        const installmentPlotId = printData.plot?._id || printData.plot;
        const correctedPaymentData = installmentPlotId ? {
          [installmentPlotId]: updatedTotals[selectedPlot?._id] || updatedTotals[Object.keys(updatedTotals)[0]]
        } : updatedTotals;

        // Create print modal data with updated totals
        const printModalData = {
          installment: updatedInstallment,
          companyInfo: selectedPlot,
          payment: correctedPaymentData // Use the corrected payment data
        };

        console.log('DEBUG: Print modal data:', printModalData);
        console.log('DEBUG: updatedTotals:', updatedTotals);
        console.log('DEBUG: selectedPlot:', selectedPlot);
        console.log('DEBUG: selectedPlot._id:', selectedPlot?._id);

        setSelectedInstallment(updatedInstallment);
        setPrintModalData(printModalData); // Store for print modal

        // Add a delay to ensure state is updated before opening print modal
        setTimeout(() => {
          setIsSlipModalOpen(true);
        }, 300);
      } else {
        console.warn('Payment response missing printData - using fallback method');
        // Fallback to old method if response doesn't have installment data
        const paidInstallment = currentInstallments.find(
          (inst) => inst._id === installmentId
        );
        if (paidInstallment) {
          // Find the full customer object for printing
          let customerForPrint = selectedCustomer;
          if (!customerForPrint && paidInstallment.customer_id) {
            // If customer_id is a string (ID), find the full customer object
            if (typeof paidInstallment.customer_id === "string") {
              customerForPrint = users.find(
                (user) => user._id === paidInstallment.customer_id
              );
            } else {
              // If it's already an object, use it directly
              customerForPrint = paidInstallment.customer_id;
            }
          }

          // Transform customer data to match the expected structure for the slip
          let transformedCustomer = null;
          if (customerForPrint) {
            transformedCustomer = {
              name:
                customerForPrint.customerData?.name ||
                customerForPrint.username ||
                "N/A",
              contactInfo: {
                phone:
                  customerForPrint.customerData?.contactInfo?.phone || "N/A",
                address:
                  customerForPrint.customerData?.contactInfo?.address || "N/A",
                cnic: customerForPrint.customerData?.contactInfo?.cnic || "N/A",
              },
            };
          }

          const updatedInstallment = {
            ...paidInstallment,
            payment_date:
              paidInstallment.payment_date || new Date().toISOString(),
            receipt_no: paymentData.receipt_no,
            payment_method: paymentData.payment_method,
            paid_amount: paymentData.paid_amount,
            // Ensure we have the full customer object for printing
            customer_id:
              transformedCustomer ||
              customerForPrint ||
              paidInstallment.customer_id,
          };
          setSelectedInstallment(updatedInstallment);

          // Add a delay to ensure state is updated before opening print modal
          setTimeout(() => {
            setIsSlipModalOpen(true);
          }, 300);
        }
      }
    } catch (err) {
      setLoading(false);
      console.error("Payment Error:", err);
      enqueueSnackbar(err.message || "Payment failed. Please try again.", {
        variant: "error",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
      });
    }
  };

  const allPendingInstallments = currentInstallments
    .filter(
      (installment) =>
        (installment.status === "Pending" || installment.status === "Overdue")
    )
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const latestAvailableInstallment = allPendingInstallments.slice(0, 1)[0];
  const filteredInstallments = allPendingInstallments;

  const CompleteInstallments = currentInstallments
    .filter(
      (installment) => installment.status === "Completed"
    )
    .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));

  const paginatedFilteredInstallments = filteredInstallments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const paginatedCompleteInstallments = CompleteInstallments.slice(
    (completedCurrentPage - 1) * itemsPerPage,
    completedCurrentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredInstallments.length / itemsPerPage);
  const completedTotalPages = Math.ceil(
    CompleteInstallments.length / itemsPerPage
  );

  const handleViewSlip = (installment) => {
    let customerForPrint = installment.customer_id;
    if (typeof installment.customer_id === "string") {
      customerForPrint = users.find(
        (user) => user._id === installment.customer_id
      );
    }
    let transformedCustomer = null;
    if (customerForPrint) {
      transformedCustomer = {
        name: customerForPrint.name || "N/A",
        contactInfo: {
          phone: customerForPrint.contactInfo?.phone || "N/A",
          address: customerForPrint.contactInfo?.address || "N/A",
          cnic: customerForPrint.contactInfo?.cnic || "N/A",
        },
      };
    }

    const installmentWithCustomer = {
      ...installment,
      customer_id:
        transformedCustomer || customerForPrint || installment.customer_id,
    };

    console.log("Customer for print:", customerForPrint);
    console.log("Transformed customer:", transformedCustomer);
    console.log("Installment with customer:", installmentWithCustomer);

    setSelectedInstallment(installmentWithCustomer);
    setIsSlipModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsSlipModalOpen(false);
    setSelectedInstallment(null);
  };

  const formatAmount = (amount) => {
    if (!amount) return "0";
    const roundedAmount = Math.round(parseFloat(amount));
    return roundedAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const getStatusColor = (status) => {
    const statusMap = {
      Installment: "text-amber-400",
    };
    return statusMap[status] || `${theme.tailwind.text}`;
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingInstallment(null);
    setEditFormData({
      installment_number: "",
      amount: "",
      due_date: "",
      description: "",
      status: "",
      paid_amount: "",
      payment_date: "",
      receipt_no: "",
    });
  };

  const formatAmountWithCommas = (value) => {
    if (!value) return "";
    return parseFloat(value).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const renderEditModal = () => {
    if (!isEditModalOpen) return null;

    const isCompleted = editFormData.status === "Completed";
    const isPending = editFormData.status === "Pending";

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div
            className={`w-full max-w-4xl mx-auto rounded-xl shadow-2xl ${gardient} border ${theme.tailwind.border2}`}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2
                  className={`text-2xl font-semibold ${theme.tailwind.text9} `}
                >
                  Edit Installment
                </h2>
                <button
                  onClick={handleCloseEditModal}
                  className={`${theme.tailwind.text9} ${theme.tailwind.hover1} transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Installment Number
                    </label>
                    <input
                      type="text"
                      name="installment_number"
                      value={editFormData.installment_number}
                      onChange={handleEditFormChange}
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                      required
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Amount (PKR)
                    </label>
                    <input
                      type="text"
                      name="amount"
                      value={formatAmountWithCommas(editFormData.amount)}
                      onChange={(e) =>
                        handleEditFormChange({
                          target: {
                            name: "amount",
                            value: e.target.value.replace(/,/g, ""),
                          },
                        })
                      }
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                      required
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Due Date
                    </label>
                    <DatePicker
                      selected={
                        editFormData.due_date
                          ? new Date(editFormData.due_date)
                          : null
                      }
                      onChange={(date) =>
                        handleEditFormChange({
                          target: {
                            name: "due_date",
                            value: date.toISOString().split("T")[0],
                          },
                        })
                      }
                      dateFormat="dd/MM/yyyy"
                      placeholderText="DD/MM/YYYY"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      wrapperClassName="w-full"
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                      maxDate={new Date()}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Status
                    </label>
                    <select
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditFormChange}
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2}`}
                      required
                    >
                      <option
                        value="Pending"
                        className={`${theme.tailwind.bg12} ${theme.tailwind.text9}`}
                      >
                        Pending
                      </option>
                      <option
                        value="Partially Paid"
                        className={`${theme.tailwind.bg12} ${theme.tailwind.text9}`}
                      >
                        Partially Paid
                      </option>
                      <option
                        value="Completed"
                        className={`${theme.tailwind.bg12} ${theme.tailwind.text9}`}
                      >
                        Completed
                      </option>
                      <option
                        value="Overdue"
                        className={`${theme.tailwind.bg12} ${theme.tailwind.text9}`}
                      >
                        Overdue
                      </option>
                    </select>
                  </div>
                </div>

                <div className="col-span-full">
                  <label
                    className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                  >
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={editFormData.description}
                    onChange={handleEditFormChange}
                    rows="3"
                    className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                  />
                </div>

                {isCompleted && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label
                          className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                        >
                          Paid Amount
                        </label>
                        <input
                          type="text"
                          name="paid_amount"
                          value={formatAmountWithCommas(
                            editFormData.paid_amount
                          )}
                          onChange={(e) =>
                            handleEditFormChange({
                              target: {
                                name: "paid_amount",
                                value: e.target.value.replace(/,/g, ""),
                              },
                            })
                          }
                          className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                        />
                      </div>
                      <div>
                        <label
                          className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                        >
                          Payment Date
                        </label>
                        <DatePicker
                          selected={
                            editFormData.payment_date
                              ? new Date(editFormData.payment_date)
                              : null
                          }
                          onChange={(date) =>
                            handleEditFormChange({
                              target: {
                                name: "payment_date",
                                value: date.toISOString().split("T")[0],
                              },
                            })
                          }
                          dateFormat="dd/MM/yyyy"
                          placeholderText="DD/MM/YYYY"
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          wrapperClassName="w-full"
                          className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                          maxDate={new Date()}
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                      >
                        Receipt No
                      </label>
                      <input
                        type="text"
                        name="receipt_no"
                        value={editFormData.receipt_no}
                        onChange={handleEditFormChange}
                        className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                      />
                    </div>
                  </>
                )}

                {isPending && (
                  <div className={`${theme.tailwind.bg17} rounded-lg p-4`}>
                    <p className={`${theme.tailwind.text9} text-sm`}>
                      Pending installments do not require paid amount or receipt
                      information.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-4 mt-8">
                <button
                  className={`px-6 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg ${theme.tailwind.bgHover10} transition-colors duration-200`}
                  onClick={handleCloseEditModal}
                >
                  Cancel
                </button>
                <button
                  className={`px-6 py-2.5 ${theme.tailwind.bg18} ${theme.tailwind.text9} rounded-lg ${theme.tailwind.bgHover11} transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]`}
                  onClick={handleSaveChanges}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPageButtons = () => {
    const buttons = [];
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${currentPage === i
            ? `${theme.tailwind.bg18} ${theme.tailwind.text9}`
            : `bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover1} hover:text-white`
            }`}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const renderCompletedPageButtons = () => {
    const buttons = [];
    let startPage = Math.max(completedCurrentPage - 1, 1);
    let endPage = Math.min(startPage + 2, completedTotalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => setCompletedCurrentPage(i)}
          className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${completedCurrentPage === i
            ? `${theme.tailwind.bg18} ${theme.tailwind.text9}`
            : `bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover1} hover:text-white`
            }`}
        >
          {i}
        </button>
      );
    }
    return buttons;
  };

  const handleCompletedPreviousPage = () => {
    if (completedCurrentPage > 1) {
      setCompletedCurrentPage(completedCurrentPage - 1);
    }
  };

  const handleCompletedNextPage = () => {
    if (completedCurrentPage < completedTotalPages) {
      setCompletedCurrentPage(completedCurrentPage + 1);
    }
  };

  const handleShowSaleHistory = async (plotId) => {
    // console.log('handleShowSaleHistory called with plotId:', plotId)
    setSaleHistoryLoading(true);
    try {
      await dispatch(fetchPlotById(plotId)).unwrap();
      setIsSaleHistoryModalOpen(true);
    } catch (error) {
      enqueueSnackbar("Failed to fetch sale history", { variant: "error" });
      console.error("Error in handleShowSaleHistory:", error);
    } finally {
      setSaleHistoryLoading(false);
    }
  };

  return (
    <div className={`p-8 ${theme.tailwind.text9}`}>
      <div className="max-w-7xl mx-auto space-y-8">
        <div className=" p-6">
          <div className="max-w-7l mx-auto">
            <div
              className={`backdrop-blur-lg bg-white/5 rounded-3xl shadow-2xl border ${theme.tailwind.border14} overflow-hidden`}
            >
              {/* Header Section */}
              <div className="relative overflow-hidden">
                <div
                  className={`absolute inset-0 ${theme.tailwind.bgr3} backdrop-blur`}
                />
                <div className="relative py-8 px-8">
                  <h1
                    className={`text-4xl font-bold text-center ${theme.tailwind.text9}`}
                  >
                    Installment Payment Management
                  </h1>
                  <p
                    className={`${theme.tailwind.text11} text-center mt-2 text-lg`}
                  >
                    {activeSociety?.name || "No Society Selected"}
                  </p>
                  <div
                    className={`h-1 w-32 mx-auto mt-4 ${theme.tailwind.text8} rounded-full`}
                  />
                </div>
              </div>

              {/* Content Section */}
              <div className="p-8 space-y-8 min-h-[45vh]">
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Payment Mode Select */}
                  <div className="space-y-2">
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Payment Mode
                    </label>
                    <select
                      value={paymentMode}
                      onChange={(e) => handlePaymentModeChange(e.target.value)}
                      className={`w-full px-4 py-3 ${theme.tailwind.bg2} ${theme.tailwind.text9} border-2 ${theme.tailwind.border15} rounded-xl focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-300 ${theme.tailwind.border16} backdrop-blur-sm`}
                      disabled
                    >
                      <option value="Installment">Installment</option>
                    </select>
                  </div>

                  {/* Customer Select */}
                  <div className="space-y-2">
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text9}`}
                    >
                      Select Customer
                    </label>
                    <select
                      value={selectedCustomer ? selectedCustomer._id : ""}
                      onChange={(e) => handleCustomerChange(e.target.value)}
                      className={`w-full px-4 py-3 ${theme.tailwind.bg2} ${theme.tailwind.text9} border-2 ${theme.tailwind.border15} rounded-xl focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-300 ${theme.tailwind.border16} backdrop-blur-sm`}
                    >
                      <option value="" className={`${theme.tailwind.bg27}`}>
                        Select Customer
                      </option>
                      {customerOptions}
                    </select>
                  </div>

                  {/* Plot Search */}
                  <div className="md:col-span-2 relative" ref={dropdownRef}>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text9} mb-2`}
                    >
                      Search Plot by Number
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={plotSearchTerm}
                        onChange={(e) => {
                          setPlotSearchTerm(e.target.value);
                          setShowPlotDropdown(true);
                        }}
                        onFocus={() => setShowPlotDropdown(true)}
                        placeholder="Enter plot number"
                        className={`w-full px-4 py-3 ${theme.tailwind.bg2} ${theme.tailwind.text9} border-2 ${theme.tailwind.border15} rounded-xl focus:ring-2 ${theme.tailwind.focusRing2} focus:border-transparent transition-all duration-300 ${theme.tailwind.border16} backdrop-blur-sm pl-10`}
                      />
                      <Tag
                        className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${theme.tailwind.text}`}
                      />
                    </div>

                    {showPlotDropdown &&
                      (plotSearchTerm || searchedPlots.length > 0) && (
                        <div className="absolute z-50 w-full mt-2">
                          <div
                            className={`${theme.tailwind.bg27} backdrop-blur-xl border ${theme.tailwind.border15} rounded-xl shadow-2xl  overflow-y-auto max-h-[calc(2*4rem)]`}
                          >
                            {searchedPlots.length > 0 ? (
                              searchedPlots.map((plot) => {
                                const plotBooking = bookings.find(
                                  (booking) => booking.plot_id?._id === plot._id
                                );
                                const customerName =
                                  plotBooking?.customer_id?.name || "N/A";

                                return (
                                  <div
                                    key={plot._id}
                                    onClick={() => {
                                      handlePlotSelect(plot);
                                      setShowPlotDropdown(false);
                                      setPlotSearchTerm(plot.plot_number);
                                    }}
                                    className={`px-4 py-3 hover:${theme.tailwind.bg3} cursor-pointer border-b border-teal-500/10 last:border-none transition-colors duration-200`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Tag
                                        className={`w-4 h-4 ${theme.tailwind.text}`}
                                      />
                                      <div className="flex-1">
                                        <p
                                          className={`${theme.tailwind.text9} font-medium`}
                                        >
                                          Plot #{plot.plot_number}
                                        </p>
                                        <p
                                          className={`${theme.tailwind.text12} text-sm`}
                                        >
                                          Customer: {customerName}
                                        </p>
                                      </div>
                                      <span
                                        className={`${theme.tailwind.text10} text-sm font-medium`}
                                      >
                                        {plot.category}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div
                                className={`px-4 py-3 ${theme.tailwind.text12} text-center`}
                              >
                                No plots found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Plot Details */}

            {(selectedCustomer || selectedPlot) && (
              <div className="w-full max-w-7xl mx-auto p-3 sm:p-4 lg:p-6">
                <div
                  className={`${theme.tailwind.bg19} rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border ${theme.tailwind.border17} shadow-xl`}
                >
                  {/* Header Section */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    <h2
                      className={`text-xl sm:text-2xl lg:text-3xl font-bold ${theme.tailwind.text9} flex items-center gap-3`}
                    >
                      <Home
                        className={`${theme.tailwind.text10} w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7`}
                      />
                      <span className="break-all">
                        {(() => {
                          const activeCustomer =
                            selectedCustomer || selectedPlotCustomer;
                          if (activeCustomer) {
                            const customerName =
                              activeCustomer.username ||
                              activeCustomer.name ||
                              activeCustomer.customerData?.name ||
                              "Customer";
                            return `${customerName}'s Plot Portfolio`;
                          }
                          return "Plot Details";
                        })()}
                      </span>
                    </h2>
                    <div
                      className={`${theme.tailwind.bg1} ${theme.tailwind.text9} px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm whitespace-nowrap`}
                    >
                      Total Plots: {filteredCustomerPlots.length}
                    </div>
                  </div>

                  {/* Empty State */}
                  {filteredCustomerPlots.length === 0 ? (
                    <div
                      className={`${theme.tailwind.bg20} rounded-xl sm:rounded-2xl py-8 sm:py-12 lg:py-16 text-center`}
                    >
                      <Info className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 mx-auto text-gray-500 mb-4" />
                      <p className="text-gray-400 text-base sm:text-lg lg:text-xl">
                        No plots found for this customer
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                      {plotCardsData.map((plotData) => {
                        const {
                          plot,
                          plotBooking,
                          plotInstallments,
                          installmentYears,
                          planEndDate,
                          isPlanExpired,
                          unpaidInstallments,
                          overdueCount,
                          cardClass,
                          details,
                          plotTotals,
                          adjustedTotalPaid
                        } = plotData;

                        return (
                          <div
                            key={plot._id}
                            onClick={() => handlePlotSelect(plot)}
                            className={`
          relative group cursor-pointer p-3 sm:p-4 lg:p-6 rounded-2xl sm:rounded-3xl
          transition-all duration-300 ease-in-out overflow-hidden
          ${cardClass}
        `}
                          >
                            {/* Gradient Overlay */}
                            <div
                              className={`absolute inset-0 ${theme.tailwind.bgGradient6} rounded-2xl sm:rounded-3xl opacity-70 group-hover:opacity-90 transition-opacity`}
                            ></div>

                            {/* Offer Tag - Properly positioned */}
                            {details?.Offer && (
                              <div className="absolute -right-12 top-5 sm:top-6 transform rotate-45 z-20">
                                <div
                                  className={`bg-red-600 ${theme.tailwind.text9} text-xs sm:text-sm font-semibold px-12 py-1 shadow-lg flex items-center justify-center gap-1`}
                                >
                                  <span>{details.Offer}</span>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-3 w-3 sm:h-4 sm:w-4"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            )}

                            {/* Plot Content */}
                            <div className="relative z-10 space-y-3 sm:space-y-4">
                              {/* Fully Paid Success Banner */}
                              {(() => {
                                const plotTotalsForBanner = totals[plot._id] || {};
                                if (plotTotalsForBanner?.isFullyPaid) {
                                  return (
                                    <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-2 sm:p-3 mb-2">
                                      <div className="flex items-center gap-2">
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                        <div className="flex-1">
                                          <p className="text-xs sm:text-sm font-semibold text-green-300">
                                            ✓ Plot Fully Paid
                                          </p>
                                          <p className="text-xs text-green-400/80 mt-0.5">
                                            All installments have been completed successfully.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              {/* Concession Applied Banner */}
                              {(() => {
                                const plotTotalsForBanner = totals[plot._id] || {};
                                if (plotTotalsForBanner?.isConcessionApplied && plotTotalsForBanner?.totalConcessionAmount > 0) {
                                  return (
                                    <div className="bg-blue-500/20 border border-blue-500/50 rounded-lg p-2 sm:p-3 mb-2">
                                      <div className="flex items-center gap-2">
                                        <Tag className="w-4 h-4 text-blue-400 flex-shrink-0" />
                                        <div className="flex-1">
                                          <p className="text-xs sm:text-sm font-semibold text-blue-300">
                                            🎁 Concession Applied
                                          </p>
                                          <p className="text-xs text-blue-400/80 mt-0.5">
                                            Total Concession: PKR {formatAmount(plotTotalsForBanner.totalConcessionAmount || 0)}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              {/* Expired Plan Warning */}
                              {(() => {
                                const plotTotalsForDuration = totals[plot._id] || {};
                                const durationInfo = calculateInstallmentDuration(plotInstallments, plotBooking, plotTotalsForDuration);
                                if (durationInfo && durationInfo.isPlanExpired && durationInfo.remaining > 0 && !durationInfo.isFullyPaid) {
                                  return (
                                    <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-2 sm:p-3 mb-2">
                                      <div className="flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0" />
                                        <div className="flex-1">
                                          <p className="text-xs sm:text-sm font-semibold text-red-300">
                                            ⚠️ Plan Period Expired
                                          </p>
                                          <p className="text-xs text-red-400/80 mt-0.5">
                                            {durationInfo.remaining} installment(s) still pending. Immediate action required.
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })()}

                              <div className="flex items-center gap-3 sm:gap-4">
                                {details?.societyImage ? (
                                  <img
                                    src={details.societyImage}
                                    alt={details.societyName}
                                    className={`w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 object-cover rounded-full border-2 sm:border-3 ${theme.tailwind.border11} shadow-lg`}
                                  />
                                ) : (
                                  <div
                                    className={`w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 ${theme.tailwind.bg22} rounded-full flex items-center justify-center`}
                                  >
                                    <Home
                                      className={`w-5 h-5 sm:w-6 sm:h-6 text-gray-500`}
                                    />
                                  </div>
                                )}

                                <div>
                                  <h3
                                    className={`text-sm sm:text-base lg:text-lg font-semibold ${theme.tailwind.text9} break-words max-w-full truncate`}
                                  >
                                    {details?.societyName}
                                  </h3>
                                  <p className="text-xs sm:text-sm text-gray-400 flex items-center">
                                    <MapPin
                                      className={`w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1.5 ${theme.tailwind.text} flex-shrink-0`}
                                    />
                                    <span className="truncate">
                                      {details?.societyLocation}
                                    </span>
                                  </p>
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center gap-2">
                                  <CheckCircle
                                    className={`w-4 h-4 sm:w-5 sm:h-5 ${theme.tailwind.text} flex-shrink-0`}
                                  />
                                  <p
                                    className={`text-xs sm:text-sm font-medium ${theme.tailwind.text9}`}
                                  >
                                    Plot Status
                                  </p>
                                </div>

                                <div
                                  className={`${theme.tailwind.bg23} rounded-lg px-3 sm:px-4 py-2 sm:py-3`}
                                >
                                  <p
                                    className={`text-xs sm:text-sm font-semibold ${getStatusColor(
                                      details?.status
                                    )} transition-colors duration-300`}
                                  >
                                    {details?.status || "Status Unknown"}
                                  </p>
                                </div>
                              </div>

                              <div className="flex justify-start">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShowCustomerDetails(
                                      selectedCustomer || selectedPlotCustomer
                                    );
                                  }}
                                  className={`flex items-center space-x-2 p-1.5 sm:p-2 rounded-lg ${theme.tailwind.bg27} ${theme.tailwind.text} text-xs sm:text-sm`}
                                  title="View Complete Customer Details"
                                >
                                  <span>View Details</span>
                                  <User size={16} className="flex-shrink-0" />
                                </button>
                                {plot.sale_history &&
                                  plot.sale_history.length > 0 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // console.log(
                                        //   'Clicked Previous Customer History',
                                        //   plot._id
                                        // )
                                        handleShowSaleHistory(plot._id);
                                      }}
                                      className={`flex items-center space-x-2 p-1.5 sm:p-2 rounded-lg ${theme.tailwind.bg27} ${theme.tailwind.text} text-xs sm:text-sm ml-2`}
                                      title="View Previous Customer History"
                                    >
                                      <span>Previous Customer History</span>
                                      <BookCopy
                                        size={16}
                                        className="flex-shrink-0"
                                      />
                                    </button>
                                  )}
                              </div>

                              {/* Plot Details */}
                              <div
                                className={`space-y-2 pt-2 sm:pt-3 border-t ${theme.tailwind.border18}`}
                              >
                                {[
                                  {
                                    icon: Tag,
                                    label: "Plot Number",
                                    value: details?.plotNumber,
                                  },
                                  {
                                    icon: Home,
                                    label: "Marla",
                                    value: details?.marla
                                      ? `${details.marla} Marla`
                                      : "N/A",
                                  },
                                  // ADD THIS NEW SECTION
                                  ...(() => {
                                    const plotTotalsForDuration = totals[plot._id] || {};
                                    const durationInfo =
                                      calculateInstallmentDuration(
                                        plotInstallments,
                                        plotBooking,
                                        plotTotalsForDuration
                                      );
                                    if (!durationInfo) return [];

                                    const isExpired = durationInfo.isPlanExpired || false;
                                    const hasPendingInstallments = durationInfo.remaining > 0;
                                    const isFullyPaid = durationInfo.isFullyPaid || false;

                                    return [
                                      {
                                        icon: CalendarIcon,
                                        label: "Installment Progress",
                                        value: isFullyPaid
                                          ? `${durationInfo.totalPlanned}/${durationInfo.totalPlanned} (Complete)`
                                          : `${durationInfo.completed}/${durationInfo.totalPlanned}`,
                                        highlight: true,
                                      },
                                      {
                                        icon: Clock10Icon,
                                        label: "Elapsed Time",
                                        value: durationInfo.formatDuration(
                                          durationInfo.elapsedMonths
                                        ),
                                        highlight: false,
                                      },
                                      {
                                        icon: Clock10Icon,
                                        label: "Remaining Duration",
                                        value: isFullyPaid
                                          ? "Plan Completed"
                                          : isExpired
                                            ? "Plan Expired"
                                            : durationInfo.formatDuration(durationInfo.remainingMonths),
                                        highlight: isExpired || isFullyPaid,
                                        warning: isExpired && hasPendingInstallments && !isFullyPaid,
                                      },
                                      {
                                        icon: Coins,
                                        label: "Remaining Installments",
                                        value: isFullyPaid ? "0 (All Paid)" : `${durationInfo.remaining}`,
                                        highlight: durationInfo.remaining > 0 && !isFullyPaid,
                                        warning: isExpired && hasPendingInstallments && !isFullyPaid,
                                      },
                                    ];
                                  })(),
                                  // END NEW SECTION
                                  {
                                    icon: SigmaIcon,
                                    label: "Initial Deposit",
                                    value: `${formatAmount(
                                      details?.deposite
                                    )} PKR`,
                                  },
                                  {
                                    icon: CheckCircle,
                                    label: "Category",
                                    value: details?.category,
                                  },
                                  {
                                    icon: BookCopy,
                                    label: "Total Paid",
                                    value: plotTotals?.isFullyPaid
                                      ? `✓ ${formatAmount(adjustedTotalPaid)} PKR (Fully Paid)`
                                      : `${formatAmount(adjustedTotalPaid)} PKR`,
                                    highlight: plotTotals?.isFullyPaid || false,
                                  },
                                  {
                                    icon: HandCoins,
                                    label: "Pending Installments",
                                    value: plotTotals?.isFullyPaid
                                      ? "0 PKR (All Paid)"
                                      : `${formatAmount(
                                        plotTotals?.totalPendingInstallmentsAmount || 0
                                      )} PKR`,
                                    highlight: plotTotals?.isFullyPaid || false,
                                  },
                                  // Show original amount if concession was applied
                                  ...(plotTotals?.isConcessionApplied && plotTotals?.originalTotalAmount && (plotTotals.originalTotalAmount || 0) > (plotTotals?.totalPlotAmount || 0) ? [{
                                    icon: Coins,
                                    label: "Original Amount",
                                    value: `${formatAmount(plotTotals.originalTotalAmount || 0)} PKR`,
                                    highlight: false,
                                  }] : []),
                                  {
                                    icon: Coins,
                                    label: plotTotals?.isConcessionApplied ? "Total Plot Amount (After Concession)" : "Total Plot Amount",
                                    value: (() => {
                                      if (plotTotals?.isConcessionApplied && plotTotals?.totalConcessionAmount > 0) {
                                        return `${formatAmount(plotTotals?.totalPlotAmount || 0)} PKR (Concession: -${formatAmount(plotTotals.totalConcessionAmount)} PKR)`;
                                      }
                                      return `${formatAmount(plotTotals?.totalPlotAmount || 0)} PKR`;
                                    })(),
                                    highlight: plotTotals?.isConcessionApplied || false,
                                  },
                                  {
                                    icon: Clock10Icon,
                                    label: "Booking Date",
                                    value: details?.Date,
                                  },
                                  {
                                    icon: ReceiptText,
                                    label: "Booking Receipt no",
                                    value: details?.bookingReceiptNo,
                                  },
                                  {
                                    icon: CalendarIcon,
                                    label: "Installments Year",
                                    value: details?.installmentsYear,
                                  },
                                  {
                                    icon: Coins,
                                    label: "Dealer Commission",
                                    value: details?.dealerCommission,
                                  },
                                  {
                                    icon: User,
                                    label: "Dealer Name",
                                    value: details?.dealerName,
                                  },
                                  {
                                    icon: Phone,
                                    label: "Dealer Phone",
                                    value: details?.dealerPhone,
                                  },
                                ].map(
                                  ({ icon: Icon, label, value, highlight, warning }) => (
                                    <div
                                      key={label}
                                      className={`flex justify-between items-center text-xs px-2 py-1 rounded transition-colors ${warning
                                        ? "bg-red-500/10 ring-1 ring-red-500/30"
                                        : highlight
                                          ? "bg-blue-500/10 ring-1 ring-blue-500/30"
                                          : ""
                                        }`}
                                    >
                                      <span
                                        className={`flex items-center gap-1 flex-shrink-0 ${warning
                                          ? "text-red-400"
                                          : highlight
                                            ? "text-blue-400"
                                            : "text-gray-300"
                                          }`}
                                      >
                                        <Icon
                                          className={`w-3 h-3 ${warning
                                            ? "text-red-400"
                                            : highlight
                                              ? "text-blue-400"
                                              : theme.tailwind.text
                                            } flex-shrink-0`}
                                        />
                                        {label}
                                      </span>
                                      <span
                                        className={`font-semibold ${warning
                                          ? "text-red-300"
                                          : highlight
                                            ? "text-blue-300"
                                            : theme.tailwind.text9
                                          } break-all text-right max-w-[60%] truncate`}
                                      >
                                        {value}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>

                              {/* Follow Up Button */}
                              {!plotTotals?.isFullyPaid && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedFollowUpData({
                                      plotId: plot._id,
                                      bookingId: plotBooking?._id || plot.booking_id,
                                      customerId: plotBooking?.customer_id?._id || plotBooking?.customer_id || plot.customer_id
                                    });
                                    setIsFollowUpModalOpen(true);
                                  }}
                                  className={`mt-4 w-full px-4 py-2 rounded-lg text-xs font-medium border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all flex items-center justify-center gap-2 group-hover:bg-blue-500/20`}
                                >
                                  <Clock10Icon className="w-3 h-3" />
                                  Follow Up / Remarks
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Follow Up Button */}
                </div>
              </div>
            )}
            <CustomerDetailsModal
              isOpen={isCustomerDetailsModalOpen}
              onClose={() => setIsCustomerDetailsModalOpen(false)}
              customer={customerDetails}
              searchedPlots={customerDetails}
            />

            {/* Installments Tables */}

            {selectedPlot && (
              <div className="space-y-10">
                <div className="relative">
                  {excessiveInstallments > 0 && (
                    <div className="bg-amber-500/20 border-l-4 border-amber-500 p-4 mb-4">
                      <div className="flex items-center">
                        <Info className="mr-3 text-amber-500" />
                        <div>
                          <p className="text-amber-300 font-medium">
                            Installment Limit Exceeded
                          </p>
                          <p className="text-amber-400 text-sm mt-1">
                            The total number of **unpaid** installments has
                            exceeded the planned limit by{" "}
                            {excessiveInstallments} month(s). Please review and
                            adjust the payments accordingly.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Alert for Overdue Installments */}
                  {overdueInstallments.length > 0 && (
                    <div className="bg-red-500/20 border-l-4 border-red-500 p-4 mb-4">
                      <div className="flex items-center">
                        <Info className="mr-3 text-red-500" />
                        <div>
                          <p className="text-red-300 font-medium">
                            Overdue Installments Detected
                          </p>
                          <p className="text-red-400 text-sm mt-1">
                            There are {overdueInstallments.length} overdue
                            installment(s). Immediate payment is required to
                            avoid penalties.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-8">
                    <h3
                      className={`text-2xl font-bold ${theme.tailwind.bgr4} bg-clip-text text-transparent`}
                    >
                      Pending Installments for Plot {selectedPlot.plot_number}{" "}
                      in{" "}
                      <span className="text-red-400 font-extrabold">
                        {selectedPlot.soc.name}
                      </span>
                    </h3>
                  </div>

                  {paginatedFilteredInstallments.length > 0 ? (
                    <div
                      className={`overflow-hidden rounded-2xl border ${theme.tailwind.border2} backdrop-blur-sm ${theme.tailwind.bg24}`}
                    >
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b ${theme.tailwind.border2}`}>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Installment Number
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Installment Amount
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Remaining Amount
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Paid Amount
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Due Date
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Status
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${theme.tailwind.divide}`}>
                          {paginatedFilteredInstallments.map((installment) => (
                            <tr
                              key={`pending-installment-${installment._id}`}
                              className={`transition-colors ${theme.tailwind.bgHover2}`}
                            >
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                {installment.installment_number}
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                <div className="space-y-1">
                                  {/* Show current amount (after concession) */}
                                  <div className="font-semibold">
                                    PKR {installment.amount.toLocaleString()}
                                  </div>
                                  {/* Show original amount and concession if concession was applied */}
                                  {installment.is_concession_applied && installment.original_amount && installment.original_amount > installment.amount && (
                                    <div className="text-xs space-y-0.5">
                                      <div className="text-gray-400 line-through">
                                        Original: PKR {installment.original_amount.toLocaleString()}
                                      </div>
                                      {installment.concession_amount > 0 && (
                                        <div className="text-green-400 font-medium">
                                          Concession: -PKR {installment.concession_amount.toLocaleString()}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                <div>
                                  PKR {Math.max(0, installment.remaining_amount || 0).toLocaleString()}
                                  {(installment.remaining_amount < 0 || installment.excess_payment > 0) && (
                                    <div className="text-xs text-green-600 mt-1">
                                      Excess: PKR {((installment.excess_payment || 0) + Math.abs(Math.min(0, installment.remaining_amount || 0))).toLocaleString()}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                PKR {installment.paid_amount.toLocaleString()}
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                {new Date(
                                  installment.due_date
                                ).toLocaleDateString("en-GB")}
                              </td>
                              <td className="py-4 px-6">
                                <div className="flex flex-col gap-2">
                                  <span
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium ${installment.status === "Completed"
                                      ? "bg-green-500/10 text-green-400 ring-1 ring-green-400/30"
                                      : installment.status === "Overdue"
                                        ? "bg-red-500/10 text-red-400 ring-1 ring-red-400/30"
                                        : "bg-yellow-500/10 text-yellow-400 ring-1 ring-yellow-400/30"
                                      }`}
                                  >
                                    {installment.status}
                                  </span>
                                  {/* Show concession badge if concession was applied */}
                                  {installment.is_concession_applied && (
                                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 ring-1 ring-blue-400/30">
                                      Concession Applied
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-6">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() =>
                                      handleInstallmentChange(installment)
                                    }
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${installment.status === "Completed" ||
                                      installment._id !==
                                      latestAvailableInstallment?._id
                                      ? `${theme.tailwind.bg3} text-white-400 cursor-not-allowed`
                                      : `${theme.tailwind.bg25} ${theme.tailwind.text9} ${theme.tailwind.bgHover13} ring-1 ${theme.tailwind.ringColor2}`
                                      }`}
                                    disabled={
                                      installment.status === "Completed" ||
                                      installment._id !==
                                      latestAvailableInstallment?._id
                                    }
                                  >
                                    {installment._id ===
                                      latestAvailableInstallment?._id
                                      ? "Pay"
                                      : "Pay Disabled"}
                                  </button>
                                  <button
                                    onClick={() => handleEditClick(installment)}
                                    className={`p-2 rounded-lg ${theme.tailwind.bg25} ${theme.tailwind.text9} ${theme.tailwind.bgHover13} ring-1 ${theme.tailwind.ringColor2} transition-all`}
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div
                      className={`text-center py-8 ${theme.tailwind.bg20} rounded-xl`}
                    >
                      <CheckCircle
                        className={`w-12 h-12 mx-auto mb-4 text-green-500`}
                      />
                      <p
                        className={`${theme.tailwind.text9} text-lg font-medium mb-2`}
                      >
                        All Installments Paid!
                      </p>
                      <p className={`${theme.tailwind.text11} text-sm`}>
                        No pending installments available for payment. All
                        installments for this plot have been completed.
                      </p>
                    </div>
                  )}

                  {/* Follow Up Button */}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-3 sm:gap-4 mt-4 sm:mt-6 mb-4 sm:mb-5 p-2 sm:p-4">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`group flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full transition-all duration-300 ${currentPage === 1
                        ? `bg-gray-600 cursor-not-allowed`
                        : `bg-white/10 ${theme.tailwind.bgHover5}`
                        }`}
                    >
                      <ChevronLeft
                        className={`w-3 h-3 sm:w-4 sm:h-4 ${currentPage === 1
                          ? `text-gray-400`
                          : `${theme.tailwind.text9} group-hover:text-white`
                          }`}
                      />
                    </button>

                    <div className="flex items-center gap-1 sm:gap-2">
                      {currentPage > 2 && totalPages > 3 && (
                        <>
                          <button
                            onClick={() => setCurrentPage(1)}
                            className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover1} hover:text-white transition-all duration-300`}
                          >
                            1
                          </button>
                          <span className={`${theme.tailwind.text9} px-1`}>
                            ...
                          </span>
                        </>
                      )}

                      {renderPageButtons()}

                      {currentPage < totalPages - 1 && totalPages > 3 && (
                        <>
                          <span className={`${theme.tailwind.text9} px-1`}>
                            ...
                          </span>
                          <button
                            onClick={() => setCurrentPage(totalPages)}
                            className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover1} hover:text-white transition-all duration-300`}
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`group flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full transition-all duration-300 ${currentPage === totalPages
                        ? `bg-gray-600 cursor-not-allowed`
                        : `bg-white/10 ${theme.tailwind.bgHover5}`
                        }`}
                    >
                      <ChevronRight
                        className={`w-3 h-3 sm:w-4 sm:h-4 ${currentPage === totalPages
                          ? `text-gray-400`
                          : `${theme.tailwind.text9} group-hover:text-white`
                          }`}
                      />
                    </button>
                  </div>
                )}

                {/* Completed Installments Section */}
                {CompleteInstallments.length > 0 && (
                  <div className="mt-12">
                    <div className="flex justify-between items-center mb-8">
                      <h3
                        className={`text-2xl font-bold ${theme.tailwind.bgr4} bg-clip-text text-transparent`}
                      >
                        Paid Installments
                      </h3>
                      <button
                        onClick={() =>
                          setIsCompletedInstallmentsPrintModalOpen(true)
                        }
                        className={`px-4 py-2 ${theme.tailwind.bg25} ${theme.tailwind.text9} ${theme.tailwind.bgHover13} ring-1 ${theme.tailwind.ringColor2} rounded-lg transition-all flex items-center space-x-2`}
                      >
                        <BookCopy size={16} />
                        <span>Print Installments</span>
                      </button>
                    </div>

                    {/* Modal for Completed Installments Print */}
                    <Modal
                      isOpen={isCompletedInstallmentsPrintModalOpen}
                      onRequestClose={() =>
                        setIsCompletedInstallmentsPrintModalOpen(false)
                      }
                      style={{
                        overlay: modalOverlayStyle,
                        content: {
                          ...modalStyle,
                          overflow: "hidden", // Remove scrolling from modal content
                          padding: "0", // Remove padding as inner div handles it
                        },
                      }}
                    >
                      <div className="relative max-h-[90vh] overflow-y-auto bg-white rounded-lg p-4">
                        <button
                          onClick={() =>
                            setIsCompletedInstallmentsPrintModalOpen(false)
                          }
                          className="absolute right-4 top-4 p-2 bg-gray-200 hover:bg-gray-300 rounded-full z-10"
                        >
                          <X size={20} className="text-gray-700" />
                        </button>
                        {selectedPlot && (
                          <CompletedInstallmentsPrint
                            installments={enrichedInstallments}
                            totalPaidAmount={totalPaidAmount}
                            totalPlotAmount={
                              totals[selectedPlot._id]?.totalPlotAmount || 0
                            }
                            adjustedTotalPaid={(() => {
                              const details = getSocietyAndPlotDetails(
                                selectedPlot._id
                              );
                              const plotTotals = totals[selectedPlot._id] || {};
                              return details?.status === "Sold"
                                ? plotTotals.totalPlotAmount || 0
                                : plotTotals.totalPaidAmount || 0;
                            })()}
                            companyInfo={selectedPlot}
                            payment={totals}
                            installmentYears={(() => {
                              const details = getSocietyAndPlotDetails(
                                selectedPlot._id
                              );
                              return details?.installmentsYear || "N/A";
                            })()}
                          />
                        )}
                      </div>
                    </Modal>

                    <div
                      className={`overflow-hidden rounded-2xl border ${theme.tailwind.border2} backdrop-blur-sm ${theme.tailwind.bg24}`}
                    >
                      <table className="w-full">
                        <thead>
                          <tr className={`border-b ${theme.tailwind.border2}`}>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Receipt No.
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Installment No.
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Amount Paid
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Payment Date
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Status
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Actions
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Payment Method
                            </th>
                            <th
                              className={`py-4 px-6 text-left text-sm font-medium ${theme.tailwind.text9}`}
                            >
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody className={`divide-y ${theme.tailwind.divide}`}>
                          {paginatedCompleteInstallments.map((installment) => (
                            <tr
                              key={`completed-installment-${installment._id}`}
                              className={`transition-colors ${theme.tailwind.bgHover2}`}
                            >
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                {installment.receipt_no}
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                {installment.installment_number}
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                <div className="space-y-1">
                                  <div className="font-semibold">
                                    PKR {installment.paid_amount.toLocaleString()}
                                  </div>
                                  {/* Show original amount and concession if concession was applied */}
                                  {installment.is_concession_applied && installment.original_amount && installment.original_amount > installment.amount && (
                                    <div className="text-xs space-y-0.5">
                                      <div className="text-gray-400 line-through">
                                        Original: PKR {installment.original_amount.toLocaleString()}
                                      </div>
                                      {installment.concession_amount > 0 && (
                                        <div className="text-green-400 font-medium">
                                          Concession: -PKR {installment.concession_amount.toLocaleString()}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                {new Date(
                                  installment.payment_date
                                ).toLocaleDateString("en-GB")}
                              </td>
                              <td className="py-4 px-6">
                                <div className="flex flex-col gap-2">
                                  <span
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium ${installment.status === "Completed"
                                      ? "bg-green-500/10 text-green-400 ring-1 ring-green-400/30"
                                      : "bg-yellow-500/10 text-yellow-400 ring-1 ring-yellow-400/30"
                                      }`}
                                  >
                                    {installment.status}
                                  </span>
                                  {/* Show concession badge if concession was applied */}
                                  {installment.is_concession_applied && (
                                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 ring-1 ring-blue-400/30">
                                      Concession Applied
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-4 px-6">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleViewSlip(installment)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium ${theme.tailwind.bg25} ${theme.tailwind.text9} ${theme.tailwind.bgHover13} ring-1 ${theme.tailwind.ringColor2} transition-all`}
                                  >
                                    View Slip
                                  </button>
                                  <button
                                    onClick={() => handleEditClick(installment)}
                                    className={`p-2 rounded-lg ${theme.tailwind.bg25} ${theme.tailwind.text9} ${theme.tailwind.bgHover13} ring-1 ${theme.tailwind.ringColor2} transition-all`}
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                </div>
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                <div className="max-w-[200px] break-words">
                                  {installment.payment_method ||
                                    "Not specified"}
                                </div>
                              </td>
                              <td
                                className={`py-4 px-6 ${theme.tailwind.text9}`}
                              >
                                <div className="max-w-[200px] break-words">
                                  {installment.description}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Completed Installments Pagination */}

                      {completedTotalPages > 1 && (
                        <div className="flex justify-center items-center gap-3 sm:gap-4 mt-4 sm:mt-6 mb-4 sm:mb-5 p-2 sm:p-4">
                          <button
                            onClick={handleCompletedPreviousPage}
                            disabled={completedCurrentPage === 1}
                            className={`group flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full transition-all duration-300 ${completedCurrentPage === 1
                              ? `bg-gray-600 cursor-not-allowed`
                              : `bg-white/10 ${theme.tailwind.bgHover5}`
                              }`}
                          >
                            <ChevronLeft
                              className={`w-3 h-3 sm:w-4 sm:h-4 ${completedCurrentPage === 1
                                ? `text-gray-400`
                                : `${theme.tailwind.text9} group-hover:text-white`
                                }`}
                            />
                          </button>

                          <div className="flex items-center gap-1 sm:gap-2">
                            {completedCurrentPage > 2 &&
                              completedTotalPages > 3 && (
                                <>
                                  <button
                                    onClick={() => setCompletedCurrentPage(1)}
                                    className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover1} hover:text-white transition-all duration-300`}
                                  >
                                    1
                                  </button>
                                  <span
                                    className={`${theme.tailwind.text9} px-1`}
                                  >
                                    ...
                                  </span>
                                </>
                              )}

                            {renderCompletedPageButtons()}

                            {completedCurrentPage < completedTotalPages - 1 &&
                              completedTotalPages > 3 && (
                                <>
                                  <span
                                    className={`${theme.tailwind.text9} px-1`}
                                  >
                                    ...
                                  </span>
                                  <button
                                    onClick={() =>
                                      setCompletedCurrentPage(
                                        completedTotalPages
                                      )
                                    }
                                    className={`flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full text-xs sm:text-sm font-medium bg-white/10 ${theme.tailwind.text9} ${theme.tailwind.bgHover1} hover:text-white transition-all duration-300`}
                                  >
                                    {completedTotalPages}
                                  </button>
                                </>
                              )}
                          </div>

                          <button
                            onClick={handleCompletedNextPage}
                            disabled={
                              completedCurrentPage === completedTotalPages
                            }
                            className={`group flex items-center justify-center h-6 w-6 sm:h-8 sm:w-8 rounded-full transition-all duration-300 ${completedCurrentPage === completedTotalPages
                              ? `bg-gray-600 cursor-not-allowed`
                              : `bg-white/10 ${theme.tailwind.bgHover5}`
                              }`}
                          >
                            <ChevronRight
                              className={`w-3 h-3 sm:w-4 sm:h-4 ${completedCurrentPage === completedTotalPages
                                ? `text-gray-400`
                                : `${theme.tailwind.text9} group-hover:text-white`
                                }`}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* PHASE-6: PREVIOUS OWNER INSTALLMENTS SECTION (RESELL PLOTS) */}
                {isResoldPlot && resellPreviousOwnerInstallments.length > 0 && (
                  <div className="mt-12">
                    <div className="flex justify-between items-center mb-8">
                      <h3
                        className={`text-2xl font-bold ${theme.tailwind.bgr4} bg-clip-text text-transparent`}
                      >
                        Previous Owner Payments
                      </h3>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium bg-orange-500/10 text-orange-400 ring-1 ring-orange-500/30`}>
                        Transferred
                      </div>
                    </div>

                    <div
                      className={`overflow-hidden rounded-2xl border ${theme.tailwind.border2} backdrop-blur-sm ${theme.tailwind.bg24}`}
                    >
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className={`${theme.tailwind.bg21} border-b ${theme.tailwind.border2}`}>
                            <tr>
                              <th className="text-left py-4 px-6 font-semibold text-sm text-gray-300">
                                Receipt No
                              </th>
                              <th className="text-left py-4 px-6 font-semibold text-sm text-gray-300">
                                Installment #
                              </th>
                              <th className="text-left py-4 px-6 font-semibold text-sm text-gray-300">
                                Amount
                              </th>
                              <th className="text-left py-4 px-6 font-semibold text-sm text-gray-300">
                                Payment Date
                              </th>
                              <th className="text-left py-4 px-6 font-semibold text-sm text-gray-300">
                                Status
                              </th>
                              <th className="text-left py-4 px-6 font-semibold text-sm text-gray-300">
                                Method
                              </th>
                              <th className="text-left py-4 px-6 font-semibold text-sm text-gray-300">
                                Description
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {processedResellPreviousOwnerInstallments.map((installment) => (
                              <tr
                                key={`previous-owner-${installment._id}`}
                                className={`transition-colors ${theme.tailwind.bgHover2} opacity-75`}
                              >
                                <td className="py-4 px-6 text-sm font-medium">
                                  {installment.receipt_no || "N/A"}
                                </td>
                                <td className="py-4 px-6 text-sm">
                                  {installment.installment_number}
                                </td>
                                <td className="py-4 px-6 text-sm">
                                  PKR {installment.paid_amount?.toLocaleString() || installment.amount?.toLocaleString()}
                                </td>
                                <td className="py-4 px-6 text-sm">
                                  {installment.payment_date
                                    ? new Date(installment.payment_date).toLocaleDateString("en-GB")
                                    : "N/A"}
                                </td>
                                <td className="py-4 px-6">
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/30">
                                    Transferred
                                  </span>
                                </td>
                                <td className="py-4 px-6 text-sm">
                                  {installment.payment_method || "N/A"}
                                </td>
                                <td className="py-4 px-6 text-sm max-w-xs truncate">
                                  {installment.description || "Previous owner payment"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <>
        <style>{printStyles}</style>
        <Modal
          isOpen={isSlipModalOpen}
          onRequestClose={handleCloseModal}
          style={{
            overlay: {
              ...modalOverlayStyle,
              padding: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            content: {
              ...modalStyle,
              width: "100%",
              maxWidth: "90vw",
              maxHeight: "90vh",
              margin: "auto",
              position: "relative",
              inset: "auto",
              overflow: "hidden", // Remove scrolling from modal content
              padding: "0", // Remove padding as inner div handles it
            },
          }}
        >
          <div className="relative w-full h-full max-h-[90vh] overflow-y-auto">
            <button
              onClick={handleCloseModal}
              className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full z-10"
            >
              <X size={20} className="text-gray-600" />
            </button>
            <div className="p-4 sm:p-6 lg:p-8">
              <InstallmentBookingSlip
                installment={printModalData?.installment || selectedInstallment}
                companyInfo={printModalData?.companyInfo || selectedPlot}
                payment={printModalData?.payment || totals}
              />
            </div>
          </div>
        </Modal>
      </>

      {/* Payment Confirmation Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div
              className={`w-full max-w-md mx-auto rounded-xl shadow-2xl ${gardient} border ${theme.tailwind.border2}`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2
                    className={`text-xl font-semibold ${theme.tailwind.text9}`}
                  >
                    Confirm Payment
                  </h2>
                  <button
                    onClick={() => {
                      setIsModalOpen(false);
                      setPaymentMode("");
                    }}
                    className={`${theme.tailwind.text9} ${theme.tailwind.hover1} transition-colors`}
                  >
                    <X size={20} />
                  </button>
                </div>

                <p className={`mb-6 ${theme.tailwind.text11}`}>
                  You are about to process a payment for installment number{" "}
                  <span className="text-red-500 font-extrabold">
                    {installmentId}
                  </span>
                  , plot number{" "}
                  <span className="text-red-500 font-extrabold">
                    {selectedPlot?.plotNumber}
                  </span>{" "}
                  in{" "}
                  <span className="text-red-500 font-extrabold">
                    {selectedPlot?.societyName}
                  </span>
                  .
                </p>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Payment Amount (PKR)
                    </label>
                    <input
                      type="text"
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                      value={paymentAmount}
                      onChange={(e) => handlePaymentAmountChange(e)}
                      placeholder="Enter payment amount"
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Receipt No.
                    </label>
                    <input
                      type="text"
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                      value={receiptNo}
                      onChange={(e) => setReceiptNo(e.target.value)}
                      placeholder="Enter receipt number"
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Payment Method
                    </label>
                    <select
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                      style={{
                        color: "#e2e8f0", // Light gray text color
                        backgroundColor: "#1e293b", // Dark background
                      }}
                    >
                      <option
                        value=""
                        style={{ backgroundColor: "#1e293b", color: "#e2e8f0" }}
                      >
                        Select Payment Method
                      </option>
                      <option
                        value="Cash"
                        style={{ backgroundColor: "#1e293b", color: "#e2e8f0" }}
                      >
                        Cash
                      </option>
                      <option
                        value="Bank Transfer"
                        style={{ backgroundColor: "#1e293b", color: "#e2e8f0" }}
                      >
                        Bank Transfer
                      </option>
                      <option
                        value="Cheque"
                        style={{ backgroundColor: "#1e293b", color: "#e2e8f0" }}
                      >
                        Cheque
                      </option>
                      <option
                        value="Online Payment"
                        style={{ backgroundColor: "#1e293b", color: "#e2e8f0" }}
                      >
                        Online Payment
                      </option>
                      <option
                        value="Mobile Banking"
                        style={{ backgroundColor: "#1e293b", color: "#e2e8f0" }}
                      >
                        Mobile Banking
                      </option>
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium ${theme.tailwind.text11} mb-2`}
                    >
                      Installment Pay Date
                    </label>
                    <DatePicker
                      selected={
                        selectedInstallmentDate
                          ? new Date(selectedInstallmentDate)
                          : null
                      }
                      onChange={(date) => setSelectedInstallmentDate(date)}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="DD/MM/YYYY"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      wrapperClassName="w-full"
                      className={`w-full px-4 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg border ${theme.tailwind.border13} ${theme.tailwind.focus3} focus:ring-1 ${theme.tailwind.focusRing2} ${theme.tailwind.placeHolder2}`}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-8">
                  <button
                    className={`px-6 py-2.5 ${theme.tailwind.bg3} ${theme.tailwind.text9} rounded-lg ${theme.tailwind.bgHover10} transition-colors duration-200`}
                    onClick={() => {
                      setIsModalOpen(false);
                      setPaymentMode("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className={`px-6 py-2.5 bg-green-500/80 ${theme.tailwind.text9
                      } rounded-lg hover:bg-green-500 transition-colors duration-200 ${loading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    onClick={handlePayment}
                    disabled={loading}
                  >
                    {loading ? "Processing..." : "Confirm Payment"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {renderEditModal()}
      {/* Sale History Modal */}
      <Modal
        isOpen={isSaleHistoryModalOpen}
        onRequestClose={() => setIsSaleHistoryModalOpen(false)}
        style={{
          overlay: modalOverlayStyle,
          content: {
            ...modalStyle,
            background: "none",
            boxShadow: "none",
            padding: 0,
          },
        }}
      >
        <div
          className={`flex min-h-full items-center justify-center p-4 bg-black/60 backdrop-blur-sm`}
        >
          <div
            className={`w-full max-w-4xl mx-auto rounded-xl shadow-2xl ${gardient} border ${theme.tailwind.border2}`}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h2
                  className={`text-2xl font-semibold ${theme.tailwind.text9}`}
                >
                  Previous Customer History
                </h2>
                <button
                  onClick={() => setIsSaleHistoryModalOpen(false)}
                  className={`${theme.tailwind.text9} ${theme.tailwind.hover1} transition-colors`}
                >
                  <X size={24} />
                </button>
              </div>
              {saleHistoryLoading ? (
                <div className={`${theme.tailwind.text}`}>Loading...</div>
              ) : !selectedPlotFromRedux?.sale_history ||
                selectedPlotFromRedux.sale_history.length === 0 ? (
                <div className={`${theme.tailwind.text}`}>
                  No previous customer history found for this plot.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className={`min-w-full border border-gray-700 rounded-lg ${theme.tailwind.bg3}`}
                  >
                    <thead>
                      <tr>
                        <th
                          className={`px-4 py-2 border-b ${theme.tailwind.text9} text-left`}
                        >
                          Plot Number
                        </th>
                        <th
                          className={`px-4 py-2 border-b ${theme.tailwind.text9} text-left`}
                        >
                          Customer Name
                        </th>
                        <th
                          className={`px-4 py-2 border-b ${theme.tailwind.text9} text-left`}
                        >
                          Sale Date
                        </th>
                        <th
                          className={`px-4 py-2 border-b ${theme.tailwind.text9} text-left`}
                        >
                          Sale Amount
                        </th>
                        <th
                          className={`px-4 py-2 border-b ${theme.tailwind.text9} text-left`}
                        >
                          Info
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPlotFromRedux.sale_history.map((entry, idx) => (
                        <tr key={idx} className={theme.tailwind.text}>
                          <td className="px-4 py-2 border-b">
                            {selectedPlotFromRedux.plot_number}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {entry.customer_id?.name || entry.customer_id}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {entry.sale_date
                              ? new Date(entry.sale_date).toLocaleDateString(
                                "en-GB"
                              )
                              : ""}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {entry.sale_amount}
                          </td>
                          <td className="px-4 py-2 border-b">
                            <button
                              className={`p-2 rounded-lg ${theme.tailwind.bg27} ${theme.tailwind.text} hover:${theme.tailwind.bg3} transition-colors`}
                              title="View Customer Info"
                              onClick={() =>
                                handleShowCustomerDetails(entry.customer_id)
                              }
                            >
                              <Eye size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
      {/* Customer Details Modal should be last for stacking */}
      <CustomerDetailsModal
        isOpen={isCustomerDetailsModalOpen}
        onClose={() => setIsCustomerDetailsModalOpen(false)}
        customer={customerDetails}
        searchedPlots={customerDetails}
      />
      {/* Follow Up Modal */}
      {selectedFollowUpData && (
        <FollowUpModal
          isOpen={isFollowUpModalOpen}
          onClose={() => {
            setIsFollowUpModalOpen(false);
            setSelectedFollowUpData(null);
          }}
          plotId={selectedFollowUpData.plotId}
          bookingId={selectedFollowUpData.bookingId}
          customerId={selectedFollowUpData.customerId}
        />
      )}
    </div>
  );
};

export default ManageInstallments;
